// L'EFFACEUR - Dashboard Admin
// Tour de contrôle pour les métriques business
// V5.44 : Notifications Admin avec Badge + Toast

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, Users, TrendingUp, Download, AlertTriangle,
  CheckCircle, Clock, XCircle, RefreshCw, BarChart3,
  Gift, Shield, Eye, ChevronDown, ChevronUp, Activity,
  Zap, Target, ArrowUpRight, ArrowDownRight, Settings,
  Link2, Send, FileText, Play, Loader2, ExternalLink, Bell,
  Ticket, Briefcase, UserPlus, Copy, Trash2, Star,
  Bot, DollarSign, Plus, Inbox, Mail, MailCheck, Sparkles,
  Gauge, AlertCircle, TrendingDown, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ToulouseSignature from '../components/shared/ToulouseSignature';
import AuditBillingPanel from '../components/admin/AuditBillingPanel';
import SupportTicketsPanel from '../components/admin/SupportTicketsPanel';
import TwoFactorSetup from '../components/admin/TwoFactorSetup';
import MaintenanceTogglePanel from '../components/admin/MaintenanceTogglePanel';
import TestRunnerPanel from '../components/admin/TestRunnerPanel';
import NotificationsTogglePanel from '../components/admin/NotificationsTogglePanel';
import E2ETestPanel from '../components/admin/E2ETestPanel';

// ============ V5.44 : NOTIFICATION CENTER ============
const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [pendingMissions, setPendingMissions] = useState(0);
  const lastCheckRef = useRef(0);
  
  // Polling pour les nouvelles notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      const newUnread = res.data.unread_count || 0;
      const newNotifs = res.data.notifications || [];
      
      // V5.44 : Toast temps réel pour nouvelles notifications
      if (newUnread > lastCheckRef.current && lastCheckRef.current > 0) {
        const latestNotif = newNotifs[0];
        if (latestNotif) {
          toast.success(latestNotif.title, {
            description: latestNotif.message,
            duration: 5000
          });
        }
      }
      
      lastCheckRef.current = newUnread;
      setUnreadCount(newUnread);
      setNotifications(newNotifs);
      setPendingMissions(res.data.pending_missions || 0);
    } catch (error) {
      console.error('Erreur notifications:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchNotifications();
    // Polling toutes les 30 secondes
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);
  
  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/notifications/mark-read`, null, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur');
    }
  };
  
  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 rounded-lg hover:bg-gray-800 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-gray-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      
      {showDropdown && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          // V1.2 : panneau notifications agrandi pour meilleure lisibilité
          className="absolute right-0 top-12 w-[460px] bg-gray-900 border border-cyan-400/30 rounded-xl shadow-2xl z-50"
          style={{ boxShadow: '0 0 40px rgba(0,245,255,0.15)' }}
        >
          <div className="p-5 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-cyan-400/10 to-transparent">
            <div>
              <h3 className="font-orbitron text-lg font-bold text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-cyan-400" />
                Notifications
              </h3>
              <p className="text-gray-500 text-xs font-mono mt-1">// CENTRE_DE_COMMANDE</p>
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-cyan-400 hover:underline font-medium">
                Tout marquer lu
              </button>
            )}
          </div>

          {/* Stats rapides */}
          <div className="p-4 border-b border-gray-800 bg-gray-800/30 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-yellow-400 font-bold text-lg leading-tight">{pendingMissions}</p>
                <p className="text-gray-500 text-xs">Mission(s) en attente</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-xs font-bold">{unreadCount}</span>
              <div>
                <p className="text-red-400 font-bold text-lg leading-tight">{unreadCount}</p>
                <p className="text-gray-500 text-xs">Non lue(s)</p>
              </div>
            </div>
          </div>

          <div className="max-h-[500px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Aucune notification pour le moment</p>
                <p className="text-xs text-gray-600 mt-1">Elles apparaîtront ici dès qu'un client fera une action</p>
              </div>
            ) : (
              notifications.map((notif, idx) => (
                <div
                  key={notif.notification_id || idx}
                  className={`p-4 border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${
                    notif.priority === 'high' ? 'bg-cyan-400/10 border-l-4 border-l-cyan-400' :
                    !notif.read ? 'bg-cyan-400/5 border-l-4 border-l-cyan-400/40' : 'border-l-4 border-l-transparent'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full mt-2 flex-shrink-0 ${
                      notif.priority === 'high' ? 'bg-cyan-400 animate-pulse shadow-lg shadow-cyan-400/50' :
                      !notif.read ? 'bg-cyan-400' : 'bg-gray-600'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white text-sm font-bold">{notif.title}</p>
                        {notif.priority === 'high' && (
                          <span className="px-2 py-0.5 text-[10px] bg-cyan-400/30 text-cyan-300 rounded font-bold tracking-wider">NEW</span>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm mt-1 leading-relaxed">{notif.message}</p>
                      <p className="text-gray-500 text-xs mt-2 font-mono">
                        {notif.created_at ? new Date(notif.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

// ============ WEBHOOK CONFIGURATION PANEL ============
const WebhookConfigPanel = () => {
  const [config, setConfig] = useState({
    enabled: true,
    mission_created_url: '',
    mission_updated_url: '',
    mandat_signed_url: '',
    document_uploaded_url: '',
    google_sheet_url: ''
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/webhooks`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        setConfig(res.data);
      } catch (error) {
        console.error('Error fetching webhook config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/webhooks`, config, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Configuration webhooks sauvegardée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTest = async (webhookType) => {
    setTesting(webhookType);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/webhooks/test?webhook_type=${webhookType}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(`Test ${webhookType} envoyé !`);
      console.log('Webhook test data:', res.data.data_sent);
    } catch (error) {
      toast.error(`Erreur test ${webhookType}`);
    } finally {
      setTesting(null);
    }
  };
  
  if (loading) {
    return (
      <div className="glass-card p-6 border border-gray-800">
        <div className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
          <span className="text-gray-400">Chargement configuration...</span>
        </div>
      </div>
    );
  }
  
  const webhooks = [
    { 
      id: 'mission_created', 
      label: 'Mission Créée', 
      field: 'mission_created_url',
      description: 'Déclenché quand un client soumet les détails de sa mission',
      icon: FileText
    },
    { 
      id: 'mission_updated', 
      label: 'Statut Mission MAJ', 
      field: 'mission_updated_url',
      description: 'Déclenché quand l\'admin change le statut d\'une mission',
      icon: Activity
    },
    { 
      id: 'mandat_signed', 
      label: 'Mandat Signé', 
      field: 'mandat_signed_url',
      description: 'Déclenché quand un client signe son mandat (inclut lien PDF)',
      icon: Shield
    },
    { 
      id: 'document_uploaded', 
      label: 'Document Uploadé', 
      field: 'document_uploaded_url',
      description: 'Déclenché quand un client uploade un document',
      icon: Download
    },
    { 
      id: 'google_sheet', 
      label: 'Google Sheets (Make/Zapier)', 
      field: 'google_sheet_url',
      description: 'URL Make.com/Zapier pour synchroniser TOUTES les données vers Google Sheets',
      icon: FileText,
      isGlobal: true
    }
  ];
  
  return (
    <div className="glass-card p-6 border border-cyan-400/30 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-400/20 rounded-lg flex items-center justify-center">
            <Link2 className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-orbitron text-lg font-bold text-white">Webhooks Génériques</h3>
            <p className="text-gray-400 text-sm">Connectez vos automatisations (Make.com, Zapier, etc.)</p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-gray-400 text-sm">Actifs</span>
          <input 
            type="checkbox" 
            checked={config.enabled}
            onChange={(e) => setConfig({...config, enabled: e.target.checked})}
            className="w-5 h-5 rounded bg-gray-800 border-gray-600 text-cyan-400 focus:ring-cyan-400"
          />
        </label>
      </div>
      
      <div className="space-y-4">
        {webhooks.map(webhook => {
          const IconComp = webhook.icon;
          return (
            <div key={webhook.id} className="bg-gray-900/50 rounded-lg p-4 border border-gray-800">
              <div className="flex items-center gap-2 mb-2">
                <IconComp className="w-4 h-4 text-cyan-400" />
                <span className="text-white font-medium">{webhook.label}</span>
              </div>
              <p className="text-gray-500 text-xs mb-3">{webhook.description}</p>
              <div className="flex gap-2">
                <Input 
                  value={config[webhook.field] || ''}
                  onChange={(e) => setConfig({...config, [webhook.field]: e.target.value})}
                  placeholder="https://hook.make.com/xxx ou https://hooks.zapier.com/xxx"
                  className="flex-1 bg-gray-800 border-gray-700 text-white text-sm"
                />
                <Button 
                  onClick={() => handleTest(webhook.id)}
                  disabled={!config[webhook.field] || testing === webhook.id}
                  variant="outline"
                  size="sm"
                  className="border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10"
                >
                  {testing === webhook.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="flex items-center justify-between pt-4 border-t border-gray-800">
        <div className="text-gray-500 text-xs">
          <p>Structure JSON envoyée : <code className="text-cyan-400">event, timestamp, client, mission, details</code></p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-cyan-400 text-black hover:bg-cyan-300"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
      </div>
    </div>
  );
};

// ============ MISSION MANAGEMENT PANEL ============
const MissionManagementPanel = () => {
  const [missions, setMissions] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [updatingMission, setUpdatingMission] = useState(null);
  const [selectedMission, setSelectedMission] = useState(null);

  // HH.4 — Search + bulk
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPack, setFilterPack] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);

  useEffect(() => {
    fetchMissions();
  }, []);

  const fetchMissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/missions`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      setMissions(res.data.missions || []);
      setStatuses(res.data.statuses || {});
      setSelectedIds(new Set());  // Reset selection on refresh
    } catch (error) {
      console.error('Error fetching missions:', error);
    } finally {
      setLoading(false);
    }
  };

  // HH.4 — Filter + search logic
  const filteredMissions = missions.filter(m => {
    if (filterStatus && m.status !== filterStatus) return false;
    if (filterPack) {
      // Mapping pack courant → tous les pack_type historiques associés
      const PACK_ALIASES = {
        demenagement: ['demenagement', 'mission_demenagement', 'depart', 'nouveau_depart'],
        heritage: ['heritage', 'mission_heritage', 'succession', 'serenite_succession'],
        audit: ['audit', 'mission_audit', 'audit_pouvoir_achat'],
      };
      const aliases = PACK_ALIASES[filterPack] || [filterPack];
      const pt = (m.pack_type || '').toLowerCase();
      if (!aliases.some(a => pt.includes(a))) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const haystack = [
        m.display_id, m.mission_id, m.user_id,
        m.client?.name, m.client?.email, m.client?.phone,
        m.status_label, m.pack_type
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredMissions.length && filteredMissions.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMissions.map(m => m.mission_id || m.user_id)));
    }
  };

  const runBulk = async () => {
    if (!bulkAction || selectedIds.size === 0) {
      toast.error('Sélectionne au moins 1 mission et une action');
      return;
    }
    if (!window.confirm(`Confirmer l'action "${bulkAction}" sur ${selectedIds.size} mission(s) ?`)) return;
    setBulkRunning(true);
    try {
      const token = localStorage.getItem('token');
      const ids = Array.from(selectedIds);
      // bulk endpoint optionnel ; fallback boucle sur status update
      if (bulkAction === 'archive' || bulkAction === 'annule' || bulkAction === 'termine' || bulkAction === 'en_cours') {
        await Promise.all(ids.map(id => axios.post(`${API}/admin/missions/update-status`,
          { mission_id: id, new_status: bulkAction === 'archive' ? 'annule' : bulkAction },
          { headers: { Authorization: `Bearer ${token}` } }
        )));
        toast.success(`${ids.length} mission(s) mises à jour`);
      } else if (bulkAction === 'dispatch') {
        await Promise.all(ids.map(id => axios.post(`${API}/admin/missions/${id}/dispatch`, {},
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => null)));
        toast.success(`Dispatch lancé pour ${ids.length} mission(s)`);
      }
      setSelectedIds(new Set());
      setBulkAction('');
      fetchMissions();
    } catch (e) {
      toast.error('Erreur action en lot');
    } finally {
      setBulkRunning(false);
    }
  };
  
  const handleStatusChange = async (missionId, newStatus) => {
    setUpdatingMission(missionId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/missions/update-status`, {
        mission_id: missionId,
        new_status: newStatus
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Statut mis à jour');
      fetchMissions();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingMission(null);
    }
  };
  
  const getStatusColor = (status) => {
    const colors = {
      nouveau: 'bg-cyan-400/20 text-cyan-400 border-cyan-400/50',
      details_submitted: 'bg-cyan-400/20 text-cyan-400 border-cyan-400/50',
      en_attente_documents: 'bg-orange-400/20 text-orange-400 border-orange-400/50',
      en_cours: 'bg-blue-400/20 text-blue-400 border-blue-400/50',
      en_cours_de_traitement: 'bg-blue-400/20 text-blue-400 border-blue-400/50',
      resiliation_envoyee: 'bg-purple-400/20 text-purple-400 border-purple-400/50',
      termine: 'bg-green-400/20 text-green-400 border-green-400/50',
      annule: 'bg-red-400/20 text-red-400 border-red-400/50'
    };
    return colors[status] || colors.nouveau;
  };
  
  const getPackIcon = (packType) => {
    // DDD.6 : ordre IMPORTANT — d'abord audit (sinon "audit" matche "depart" via 't'),
    // puis déménagement (priorité haute), puis héritage. Le bundle Démén+Audit
    // contient "demenagement" et "audit" → on le classe comme déménagement (point d'entrée).
    if (!packType) return '📊';
    const p = packType.toLowerCase();
    if (p.includes('demenagement') || p.includes('depart') || p.includes('bundle')) return '🏠';
    if (p.includes('heritage') || p.includes('succession')) return '📜';
    if (p.includes('audit')) return '📊';
    return '📊';
  };
  
  if (loading) {
    return (
      <div className="glass-card p-6 border border-gray-800">
        <div className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
          <span className="text-gray-400">Chargement des missions...</span>
        </div>
      </div>
    );
  }
  
  return (
    <>
    {/* CCC.1 + CCC.3 — Outils de test E2E (active mission gratuite + reset par user) */}
    <E2ETestPanel />

    <div className="glass-card border border-cyan-400/30 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-900/80 p-4 border-b border-gray-800 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-400/20 rounded-lg flex items-center justify-center">
            <Target className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-orbitron text-lg font-bold text-white">Gestion des Missions</h3>
            <p className="text-gray-400 text-sm">{filteredMissions.length}/{missions.length} mission(s){selectedIds.size > 0 && ` · ${selectedIds.size} sélectionnée(s)`}</p>
          </div>
        </div>
        <Button onClick={fetchMissions} variant="outline" size="sm" className="border-gray-700">
          <RefreshCw className="w-4 h-4 mr-1" />Actualiser
        </Button>
      </div>

      {/* HH.4 — Search + filters bar */}
      <div className="bg-gray-900/40 p-3 border-b border-gray-800 space-y-2">
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher : nom, email, téléphone, ID mission..."
            className="flex-1 min-w-[200px] px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-cyan-400 focus:outline-none"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm"
          >
            <option value="">Tous statuts</option>
            {Object.entries(statuses).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
          <select
            value={filterPack}
            onChange={(e) => setFilterPack(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm"
          >
            <option value="">Tous packs</option>
            <option value="demenagement">Déménagement</option>
            <option value="heritage">Héritage</option>
            <option value="audit">Audit</option>
          </select>
        </div>

        {/* Bulk actions bar — visible si au moins 1 sélection */}
        {selectedIds.size > 0 && (
          <div className="flex gap-2 items-center flex-wrap pt-2 border-t border-gray-800">
            <span className="text-xs font-mono text-cyan-400">{selectedIds.size} sélectionnée(s) :</span>
            <select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              className="px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-xs"
            >
              <option value="">— Choisir une action —</option>
              <option value="dispatch">Dispatch agent (lancer traitement)</option>
              <option value="en_cours">Marquer "En cours"</option>
              <option value="termine">Marquer "Terminé"</option>
              <option value="annule">Annuler</option>
            </select>
            <Button
              onClick={runBulk}
              disabled={bulkRunning || !bulkAction}
              size="sm"
              className="bg-magenta-400 text-white hover:bg-magenta-500 font-bold text-xs"
            >
              {bulkRunning ? 'Exécution...' : 'Appliquer'}
            </Button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-gray-500 hover:text-white"
            >
              Désélectionner tout
            </button>
          </div>
        )}
      </div>

      {/* Missions List */}
      <div className="divide-y divide-gray-800">
        {filteredMissions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {missions.length === 0 ? 'Aucune mission en cours' : 'Aucun résultat pour ces filtres'}
          </div>
        ) : (
          <>
            {/* HH.4 — Header sélection globale */}
            <div className="p-2 px-4 bg-gray-900/30 flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedIds.size === filteredMissions.length && filteredMissions.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 accent-cyan-400"
              />
              <span className="text-xs font-mono text-gray-500">Tout sélectionner ({filteredMissions.length})</span>
            </div>
            {filteredMissions.map((mission, missionIndex) => (
            <div key={mission.mission_id || `mission-${missionIndex}-${mission.user_id}`} className="p-4 hover:bg-gray-900/30 transition-colors">
              {/* V5.59 : Mission Header simplifié - Référence, Client, Statut, Date */}
              <div className="flex items-center justify-between gap-4">
                <input
                  type="checkbox"
                  checked={selectedIds.has(mission.mission_id || mission.user_id)}
                  onChange={() => toggleSelect(mission.mission_id || mission.user_id)}
                  className="w-4 h-4 accent-cyan-400 shrink-0"
                />
                <div className="flex items-center gap-3 flex-1">
                  {/* Icône Pack */}
                  <span className="text-2xl">{getPackIcon(mission.pack_type)}</span>
                  
                  {/* Info principale */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      {/* Référence Dossier */}
                      <span className="font-mono text-cyan-400 text-sm font-bold">
                        {mission.display_id || mission.mission_id?.substring(0, 15)}
                      </span>
                      {/* V5.59 : Badge statut amélioré - "nouveau" mis en valeur */}
                      <span className={`px-2 py-0.5 text-xs rounded border font-medium ${
                        mission.status === 'nouveau' 
                          ? 'bg-cyan-400/30 text-cyan-300 border-cyan-400 animate-pulse' 
                          : getStatusColor(mission.status)
                      }`}>
                        {mission.status_label}
                      </span>
                      {/* V5.68 : Pastille rouge "Message client non lu" */}
                      {mission.has_unread_client_message && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/30 text-red-400 border border-red-500/50 text-xs rounded font-bold animate-pulse">
                          <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                          Message
                        </span>
                      )}
                    </div>
                    <p className="text-white font-medium truncate">{mission.client?.name || mission.client?.email || 'Client'}</p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {mission.created_at ? new Date(mission.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </p>
                  </div>
                </div>
                
                {/* V5.59 : Actions simplifiées */}
                <div className="flex items-center gap-2">
                  {/* Sélecteur de statut */}
                  <select
                    value={mission.status || 'nouveau'}
                    onChange={(e) => handleStatusChange(mission.mission_id || mission.user_id, e.target.value)}
                    disabled={updatingMission === mission.user_id}
                    className="bg-gray-800 border border-gray-700 text-white text-sm rounded px-2 py-1 focus:border-cyan-400 focus:outline-none"
                  >
                    {Object.entries(statuses).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                  {updatingMission === mission.user_id && (
                    <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                  )}
                  
                  {/* V5.59 : Bouton Voir Détails uniquement */}
                  <Link
                    to={`/admin/missions/${mission.mission_id || mission.user_id}`}
                    className="flex items-center gap-1 px-3 py-1 bg-cyan-400/20 text-cyan-400 text-sm rounded hover:bg-cyan-400/30 transition-colors font-medium"
                  >
                    <Eye className="w-4 h-4" />Détails
                  </Link>
                </div>
              </div>
            </div>
            ))}
          </>
        )}
      </div>
    </div>
    </>
  );
};

const PACKS = {
  mission_audit: { name: "Mission Audit", price: 0 },
  mission_demenagement: { name: "Mission Déménagement", price: 149 },
  mission_heritage: { name: "Mission Héritage", price: 199 },
  nouveau_depart: { name: "Mission Déménagement", price: 59 },
  serenite_succession: { name: "Sérénité Succession", price: 199 },
  audit_pouvoir_achat: { name: "Audit Pouvoir d'Achat", price: 0 }
};

// ============ TRACKER DE CONVERSIONS EN TEMPS RÉEL ============
const ConversionTracker = ({ data, clicksByPartner }) => {
  const [liveUpdates, setLiveUpdates] = useState([]);
  const [timeRange, setTimeRange] = useState('24h');
  
  // Simuler les mises à jour en temps réel (sera remplacé par WebSocket quand les vraies clés seront actives)
  useEffect(() => {
    // Générer quelques événements de démonstration basés sur les vraies données
    const generateDemoEvents = () => {
      const partners = ['Awin', 'Kwanko', 'Effinity', 'Direct'];
      const types = ['click', 'conversion', 'pending'];
      const now = new Date();
      
      return Array.from({ length: 5 }, (_, i) => {
        const eventType = types[Math.floor(Math.random() * types.length)];
        return {
          id: `evt_${Date.now()}_${i}`,
          type: eventType,
          partner: partners[Math.floor(Math.random() * partners.length)],
          amount: eventType === 'conversion' ? (Math.random() * 50 + 10).toFixed(2) : null,
          timestamp: new Date(now - Math.random() * 3600000 * 24),
          status: Math.random() > 0.3 ? 'success' : 'pending'
        };
      });
    };
    
    setLiveUpdates(generateDemoEvents());
    
    // Actualiser toutes les 30 secondes
    const interval = setInterval(() => {
      setLiveUpdates(prev => {
        const newEvent = {
          id: `evt_${Date.now()}`,
          type: Math.random() > 0.7 ? 'conversion' : 'click',
          partner: ['Awin', 'Kwanko', 'Effinity'][Math.floor(Math.random() * 3)],
          amount: Math.random() > 0.7 ? (Math.random() * 50 + 10).toFixed(2) : null,
          timestamp: new Date(),
          status: 'success'
        };
        return [newEvent, ...prev.slice(0, 9)];
      });
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Calculer les stats par régie
  const regieStats = [
    { 
      name: 'Awin', 
      color: 'cyan',
      clicks: clicksByPartner.filter(p => p.network === 'awin').reduce((sum, p) => sum + p.total_clicks, 0),
      conversions: clicksByPartner.filter(p => p.network === 'awin').reduce((sum, p) => sum + p.conversions, 0),
      commission: clicksByPartner.filter(p => p.network === 'awin').reduce((sum, p) => sum + (p.commission || 0), 0),
      trend: '+12%',
      trendUp: true
    },
    { 
      name: 'Kwanko', 
      color: 'magenta',
      clicks: clicksByPartner.filter(p => p.network === 'kwanko').reduce((sum, p) => sum + p.total_clicks, 0),
      conversions: clicksByPartner.filter(p => p.network === 'kwanko').reduce((sum, p) => sum + p.conversions, 0),
      commission: clicksByPartner.filter(p => p.network === 'kwanko').reduce((sum, p) => sum + (p.commission || 0), 0),
      trend: '+8%',
      trendUp: true
    },
    { 
      name: 'Effinity', 
      color: 'success',
      clicks: clicksByPartner.filter(p => p.network === 'effinity').reduce((sum, p) => sum + p.total_clicks, 0),
      conversions: clicksByPartner.filter(p => p.network === 'effinity').reduce((sum, p) => sum + p.conversions, 0),
      commission: clicksByPartner.filter(p => p.network === 'effinity').reduce((sum, p) => sum + (p.commission || 0), 0),
      trend: '-3%',
      trendUp: false
    }
  ];
  
  const totalCommission = regieStats.reduce((sum, r) => sum + r.commission, 0);
  
  return (
    <section className="space-y-4">
      {/* Header with Live indicator */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-lg font-bold text-white flex items-center gap-2">
          <Activity className="w-5 h-5 text-success" />
          Tracker Conversions
          <span className="flex items-center gap-1 ml-2">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-success text-xs font-mono">LIVE</span>
          </span>
        </h2>
        <div className="flex gap-2">
          {['24h', '7j', '30j'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 text-xs font-mono rounded transition-all ${
                timeRange === range 
                  ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50' 
                  : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>
      
      {/* Performance par Régie */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {regieStats.map((regie, idx) => (
          <motion.div
            key={regie.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className={`glass-card p-4 border-2 transition-all hover:shadow-lg ${
              regie.color === 'cyan' ? 'border-cyan-400/30 hover:border-cyan-400/60 hover:shadow-cyan-400/20' :
              regie.color === 'magenta' ? 'border-magenta-400/30 hover:border-magenta-400/60 hover:shadow-magenta-400/20' :
              'border-success/30 hover:border-success/60 hover:shadow-success/20'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-orbitron text-sm font-bold ${
                regie.color === 'cyan' ? 'text-cyan-400' :
                regie.color === 'magenta' ? 'text-magenta-400' :
                'text-success'
              }`}>
                {regie.name}
              </h3>
              <div className={`flex items-center gap-1 text-xs font-mono ${
                regie.trendUp ? 'text-success' : 'text-red-400'
              }`}>
                {regie.trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {regie.trend}
              </div>
            </div>
            
            {/* Mini bar chart */}
            <div className="h-12 flex items-end gap-1 mb-3">
              {Array.from({ length: 12 }, (_, i) => {
                const height = Math.random() * 80 + 20;
                return (
                  <div
                    key={i}
                    className={`flex-1 rounded-t transition-all ${
                      regie.color === 'cyan' ? 'bg-cyan-400/40 hover:bg-cyan-400/60' :
                      regie.color === 'magenta' ? 'bg-magenta-400/40 hover:bg-magenta-400/60' :
                      'bg-success/40 hover:bg-success/60'
                    }`}
                    style={{ height: `${height}%` }}
                  />
                );
              })}
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold text-white">{regie.clicks}</p>
                <p className="text-xs text-gray-500">Clics</p>
              </div>
              <div>
                <p className="text-lg font-bold text-success">{regie.conversions}</p>
                <p className="text-xs text-gray-500">Conv.</p>
              </div>
              <div>
                <p className="text-lg font-bold text-yellow-400">{regie.commission.toFixed(0)}€</p>
                <p className="text-xs text-gray-500">Comm.</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Live Feed */}
      <div className="glass-card border border-gray-800 overflow-hidden">
        <div className="bg-gray-900/80 px-4 py-2 border-b border-gray-800 flex items-center justify-between">
          <span className="text-xs font-mono text-gray-400">// FLUX_EN_DIRECT</span>
          <span className="text-xs text-gray-500">{liveUpdates.length} événements</span>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {liveUpdates.map((event, idx) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="px-4 py-2 border-b border-gray-800/50 hover:bg-gray-900/30 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${
                  event.type === 'conversion' ? 'bg-success' :
                  event.type === 'click' ? 'bg-cyan-400' :
                  'bg-yellow-400'
                }`} />
                <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                  event.type === 'conversion' ? 'bg-success/20 text-success' :
                  event.type === 'click' ? 'bg-cyan-400/20 text-cyan-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {event.type === 'conversion' ? 'CONV' : event.type === 'click' ? 'CLIC' : 'PEND'}
                </span>
                <span className="text-sm text-gray-300">{event.partner}</span>
                {event.amount && (
                  <span className="text-sm font-bold text-success">+{event.amount}€</span>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {event.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Total Commission Card */}
      <div className="glass-card p-4 border-2 border-yellow-500/30 bg-gradient-to-r from-yellow-500/5 to-transparent">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm mb-1">Commission Totale Générée</p>
            <p className="font-orbitron text-3xl font-black text-white" style={{ textShadow: '0 0 20px rgba(234, 179, 8, 0.4)' }}>
              {totalCommission.toFixed(2)}€
            </p>
          </div>
          <div className="text-right">
            <p className="text-gray-500 text-xs mb-1">Taux de conversion moyen</p>
            <p className="text-2xl font-bold text-success">
              {data?.affiliation?.conversion_rate || 0}%
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — OrganismCatalogPanel : Gestion évolutive du catalogue (MongoDB + statique)
// ═══════════════════════════════════════════════════════════════════════
const OrganismCatalogPanel = () => {
  const [organisms, setOrganisms] = useState([]);
  const [stats, setStats] = useState({});
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    id: '', name: '', category: '', channel_preferred: 'email',
    email_resiliation: '', portal_url: '', lrar_address: '', sla_days: 30, notes: '',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [catRes, unmRes] = await Promise.all([
        axios.get(`${API}/admin/agent/catalog/all`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
        axios.get(`${API}/admin/agent/unmatched`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
      ]);
      setOrganisms(catRes.data.organisms || []);
      setStats(catRes.data.stats || {});
      setUnmatched(unmRes.data.unmatched || []);
    } catch (e) {
      toast.error('Erreur chargement catalogue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const startEdit = (org) => {
    setEditing(org.id);
    setForm({
      id: org.id || '',
      name: org.name || '',
      category: org.category || '',
      channel_preferred: org.channel_preferred || 'email',
      email_resiliation: org.email_resiliation || '',
      portal_url: org.portal_url || '',
      lrar_address: org.lrar_address || '',
      sla_days: org.sla_days || 30,
      notes: org.notes || '',
    });
  };

  const startNew = () => {
    setEditing('__new__');
    setForm({ id: '', name: '', category: '', channel_preferred: 'email', email_resiliation: '', portal_url: '', lrar_address: '', sla_days: 30, notes: '' });
  };

  const saveForm = async () => {
    if (!form.id || !form.name) { toast.error('id et name obligatoires'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/agent/catalog`, form, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success(`Organisme ${form.name} enregistré`);
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    }
  };

  const quickCreateFromUnmatched = async (provider_name) => {
    const email = window.prompt(`Email de résiliation pour "${provider_name}" ? (laisser vide si aucun)`);
    const lrar = email ? '' : window.prompt(`Adresse LRAR pour "${provider_name}" ?`);
    const category = window.prompt(`Catégorie ? (electricite, gaz, internet, assurance, banque, mutuelle, sport, presse, autre)`) || 'autre';
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/agent/catalog/quick-create`,
        { name: provider_name, email_resiliation: email || null, lrar_address: lrar || null, category },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success(res.data.agent_message || 'Créé');
      await axios.post(`${API}/admin/agent/unmatched/${encodeURIComponent(provider_name)}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const deleteOrganism = async (id) => {
    if (!window.confirm(`Supprimer "${id}" ?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/agent/catalog/${id}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success('Supprimé');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const filtered = organisms.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.name || '').toLowerCase().includes(s)
      || (o.id || '').toLowerCase().includes(s)
      || (o.category || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Rechercher un organisme…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm bg-gray-900 border-gray-700 text-white"
        />
        <Button onClick={startNew} size="sm" className="bg-cyan-500 text-black hover:bg-cyan-400">
          <UserPlus className="w-4 h-4 mr-1" /> Nouveau
        </Button>
        <Button onClick={fetchAll} variant="outline" size="sm" className="border-gray-700 text-gray-400">
          <RefreshCw className="w-4 h-4 mr-1" /> Rafraîchir
        </Button>
        <div className="ml-auto text-xs font-mono text-gray-500">
          {stats.total || 0} organismes ·
          statique {stats.by_source?.static || 0} ·
          custom {stats.by_source?.custom || 0} ·
          overridden {stats.by_source?.overridden || 0}
        </div>
      </div>

      {/* Organismes inconnus signalés */}
      {unmatched.length > 0 && (
        <div className="glass-card border border-orange-500/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            <h3 className="font-orbitron text-sm font-bold text-orange-400">
              Organismes inconnus détectés par l'agent — {unmatched.length}
            </h3>
          </div>
          <div className="space-y-2">
            {unmatched.slice(0, 10).map((u, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-900/50 p-3 rounded">
                <div>
                  <p className="text-white font-mono">{u.provider_name}</p>
                  <p className="text-gray-500 text-xs">
                    {u.occurrences} occurrence(s) · catégorie hint : {u.category_hint || '—'}
                  </p>
                </div>
                <Button
                  onClick={() => quickCreateFromUnmatched(u.provider_name)}
                  size="sm"
                  className="bg-orange-500 text-white hover:bg-orange-600"
                >
                  Créer
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form d'édition */}
      {editing && (
        <div className="glass-card border border-cyan-500/40 p-4 space-y-3">
          <h3 className="text-cyan-400 font-orbitron text-sm font-bold">
            {editing === '__new__' ? 'Nouvel organisme' : `Édition : ${form.name}`}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="id (ex: edf, orange)" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} className="bg-gray-900 border-gray-700 text-white" disabled={editing !== '__new__'} />
            <Input placeholder="Nom commercial" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
            <select value={form.channel_preferred} onChange={(e) => setForm({ ...form, channel_preferred: e.target.value })} className="bg-gray-900 border border-gray-700 text-white rounded px-3 py-2">
              <option value="email">Canal : email</option>
              <option value="lrar">Canal : LRAR</option>
              <option value="portal">Canal : portail</option>
              <option value="form">Canal : formulaire</option>
            </select>
            <Input placeholder="Email résiliation" value={form.email_resiliation} onChange={(e) => setForm({ ...form, email_resiliation: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="URL portail" value={form.portal_url} onChange={(e) => setForm({ ...form, portal_url: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="Adresse LRAR" value={form.lrar_address} onChange={(e) => setForm({ ...form, lrar_address: e.target.value })} className="bg-gray-900 border-gray-700 text-white md:col-span-2" />
            <Input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-gray-900 border-gray-700 text-white md:col-span-2" />
          </div>
          <div className="flex gap-2">
            <Button onClick={saveForm} className="bg-cyan-500 text-black hover:bg-cyan-400">Enregistrer</Button>
            <Button onClick={() => setEditing(null)} variant="outline" className="border-gray-700 text-gray-400">Annuler</Button>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="text-center py-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Chargement…</div>
      ) : (
        <div className="glass-card border border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/80">
              <tr>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">ID</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Nom</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Catégorie</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Canal</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Contact</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Source</th>
                <th className="text-right p-3 text-gray-500 font-mono text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 200).map(o => (
                <tr key={o.id} className="border-t border-gray-800/50 hover:bg-gray-900/30">
                  <td className="p-3 text-cyan-400 font-mono text-xs">{o.id}</td>
                  <td className="p-3 text-white">{o.name}</td>
                  <td className="p-3 text-gray-400">{o.category}</td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 rounded bg-gray-800 text-gray-400 font-mono text-xs">{o.channel_preferred}</span>
                  </td>
                  <td className="p-3 text-xs text-gray-500 font-mono max-w-xs truncate">
                    {o.email_resiliation || o.lrar_address || o.portal_url || '—'}
                  </td>
                  <td className="p-3 text-xs">
                    <span className={`font-mono ${o.source === 'custom' ? 'text-fuchsia-400' : o.source === 'overridden' ? 'text-yellow-400' : 'text-gray-500'}`}>
                      {o.source}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <Button onClick={() => startEdit(o)} variant="outline" size="sm" className="border-gray-700 text-cyan-400 hover:bg-cyan-500/10 mr-2">Éditer</Button>
                    <Button onClick={() => deleteOrganism(o.id)} variant="outline" size="sm" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 200 && (
            <p className="text-center text-gray-500 text-xs py-3">{filtered.length - 200} organismes supplémentaires — affinez la recherche.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — AgentSchedulerPanel : toggle scheduler automatique
// ═══════════════════════════════════════════════════════════════════════
const AgentSchedulerPanel = () => {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchState = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/agent/scheduler`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setSettings(res.data.settings);
      setStatus(res.data.status);
    } catch (e) {
      toast.error('Erreur chargement scheduler');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  const toggle = async (key) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/agent/scheduler`, { [key]: !settings[key] }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setSettings(res.data.settings);
      toast.success(`${key} : ${res.data.settings[key] ? 'ON' : 'OFF'}`);
    } catch (e) {
      toast.error('Erreur toggle');
    }
  };

  if (loading || !settings) return <div className="text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />…</div>;

  const toggles = [
    { key: 'enabled', label: 'Scheduler global (master switch)' },
    { key: 'auto_dispatch', label: 'Dispatch automatique (toutes les 15 min)' },
    { key: 'auto_followup', label: 'Relances automatiques (toutes les 4h)' },
    { key: 'daily_summary', label: 'Email de synthèse quotidienne (08:00 UTC)' },
  ];

  return (
    <div className="space-y-4">
      <div className="glass-card border border-gray-800 p-5 space-y-3">
        <h3 className="font-orbitron text-sm font-bold text-white flex items-center gap-2">
          <Bot className="w-4 h-4 text-fuchsia-400" /> Configuration de l'autonomie
        </h3>
        {toggles.map(t => (
          <div key={t.key} className="flex items-center justify-between py-2 border-b border-gray-800/50">
            <span className="text-gray-300 text-sm">{t.label}</span>
            <button
              onClick={() => toggle(t.key)}
              className={`px-4 py-1.5 rounded font-mono text-xs font-bold ${settings[t.key] ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-gray-800 text-gray-500 border border-gray-700'}`}
            >
              {settings[t.key] ? 'ACTIVÉ' : 'DÉSACTIVÉ'}
            </button>
          </div>
        ))}
      </div>

      {status && (
        <div className="glass-card border border-gray-800 p-5">
          <h3 className="font-orbitron text-sm font-bold text-white mb-3">État des jobs</h3>
          <div className="space-y-2 text-xs font-mono">
            <p>Scheduler en cours : <span className={status.running ? 'text-green-400' : 'text-red-400'}>{status.running ? 'YES' : 'NO'}</span></p>
            {(status.jobs || []).map(j => (
              <div key={j.id} className="flex justify-between">
                <span className="text-gray-400">{j.id}</span>
                <span className="text-cyan-400">prochaine : {j.next_run_time ? new Date(j.next_run_time).toLocaleString('fr-FR') : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — AgentQueuePanel : File d'attente des actions opérateur / agent IA
// ═══════════════════════════════════════════════════════════════════════
const PRIORITY_COLORS = {
  P0: 'border-red-500/60 bg-red-500/10 text-red-400',
  P1: 'border-orange-500/60 bg-orange-500/10 text-orange-400',
  P2: 'border-yellow-500/60 bg-yellow-500/10 text-yellow-400',
  P3: 'border-green-500/60 bg-green-500/10 text-green-400',
};

const AgentQueuePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [executing, setExecuting] = useState(null);
  const [lastReport, setLastReport] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (priorityFilter) params.set('priority', priorityFilter);
      if (actionFilter) params.set('action', actionFilter);
      const res = await axios.get(`${API}/admin/agent/tasks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement file agent');
    } finally {
      setLoading(false);
    }
  }, [priorityFilter, actionFilter]);

  const fetchCatalog = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/agent/organism-catalog`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setCatalog(res.data.organisms || []);
    } catch (e) {
      // silencieux : le catalogue est optionnel
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    fetchCatalog();
  }, [fetchQueue, fetchCatalog]);

  const runExecute = async (task) => {
    if (!task?.mission_id) return;
    const ACTION_MAP = {
      DISPATCH_MANDATS: { path: 'dispatch', label: 'dispatch mandats' },
      FOLLOW_UP_ORGANISM: { path: 'followup', label: 'relance' },
      VALIDATE_CLOSURE: { path: 'close', label: 'clôture' },
    };
    const action = ACTION_MAP[task.action_type];
    if (!action) {
      toast.error('Action non automatisable — intervention manuelle requise.');
      return;
    }
    if (!window.confirm(`Lancer ${action.label} pour la mission ${task.mission_id?.slice(0, 12)}… ?`)) return;
    setExecuting(task.task_id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/agent/execute/${action.path}/${task.mission_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      setLastReport(res.data);
      const sent = res.data.sent_email?.length || 0;
      const lrar = res.data.prepared_lrar?.length || 0;
      const manual = res.data.manual_portal?.length || 0;
      const relances = res.data.relances?.length || 0;
      toast.success(
        res.data.status === 'success'
          ? `Agent exécuté : ${sent} email(s) envoyé(s), ${lrar} LRAR prêt(s), ${manual} manuel(s), ${relances} relance(s)`
          : `Rapport : ${res.data.status}`
      );
      fetchQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur exécution agent');
    } finally {
      setExecuting(null);
    }
  };

  const tasks = data?.tasks || [];
  const stats = data?.stats || { total: 0, by_priority: {}, by_action: {} };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-1 h-8 bg-fuchsia-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(255,0,255,0.8)' }} />
        <h2 className="font-orbitron text-xl font-black text-white">Agent / Opérateur</h2>
        <span className="font-mono text-xs text-fuchsia-400/60">// FILE_ATTENTE · DISPATCH · SLA</span>
      </div>

      {/* Stats en bandeau */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['P0', 'P1', 'P2', 'P3'].map(p => (
          <button
            key={p}
            onClick={() => setPriorityFilter(priorityFilter === p ? '' : p)}
            className={`glass-card p-4 border transition-all ${priorityFilter === p ? PRIORITY_COLORS[p] : 'border-gray-800 hover:border-gray-700'}`}
          >
            <p className="font-mono text-xs text-gray-400 mb-1">{p} — {p === 'P0' ? 'URGENT' : p === 'P1' ? 'HIGH' : p === 'P2' ? 'MEDIUM' : 'LOW'}</p>
            <p className="font-orbitron text-3xl font-black text-white">{stats.by_priority?.[p] || 0}</p>
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="bg-gray-900 border border-gray-700 text-white text-sm rounded px-3 py-2 font-mono"
        >
          <option value="">Tous types d'action</option>
          <option value="DISPATCH_MANDATS">Envoyer mandats</option>
          <option value="FOLLOW_UP_ORGANISM">Relancer organisme</option>
          <option value="COLLECT_DOCUMENT">Document manquant</option>
          <option value="VALIDATE_CLOSURE">Valider clôture</option>
          <option value="MANUAL_REVIEW">Revue manuelle</option>
        </select>
        <Button
          onClick={fetchQueue}
          variant="outline"
          size="sm"
          className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
        </Button>
        <span className="text-xs text-gray-500 font-mono ml-auto">
          Catalogue : {catalog.length} organismes
        </span>
      </div>

      {/* Liste des tâches */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Chargement…
        </div>
      ) : tasks.length === 0 ? (
        <div className="glass-card border border-gray-800 p-10 text-center">
          <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
          <p className="text-gray-300 font-mono">Aucune action en attente — tout est à jour.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map(t => (
            <div
              key={t.task_id}
              className={`glass-card border-l-4 p-4 ${PRIORITY_COLORS[t.priority] || 'border-gray-700'}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold ${PRIORITY_COLORS[t.priority]}`}>
                      {t.priority_label}
                    </span>
                    <span className="text-white font-orbitron text-sm font-bold">{t.label}</span>
                  </div>
                  <p className="text-gray-400 text-xs mb-2">{t.description}</p>
                  <div className="flex flex-wrap gap-3 text-xs font-mono text-gray-500">
                    <span>Mission : <span className="text-cyan-400">{t.mission_id?.slice(0, 12)}…</span></span>
                    {t.user_email && <span>Client : <span className="text-white">{t.user_email}</span></span>}
                    {t.mission_type && <span>Type : <span className="text-magenta-400">{t.mission_type}</span></span>}
                    {t.hours_since !== undefined && <span>Depuis : <span className="text-yellow-400">{t.hours_since}h</span></span>}
                    {t.days_since !== undefined && <span>Depuis : <span className="text-yellow-400">{t.days_since}j</span></span>}
                  </div>
                  {Array.isArray(t.categories) && t.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {t.categories.slice(0, 6).map((c, i) => (
                        <span key={i} className="px-2 py-0.5 rounded bg-gray-800 text-gray-400 text-xs font-mono">
                          {typeof c === 'string' ? c : c?.category_id || c?.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {Array.isArray(t.missing_documents) && t.missing_documents.length > 0 && (
                    <div className="mt-2 text-xs text-orange-400 font-mono">
                      Docs manquants : {t.missing_documents.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Link
                    to={`/admin/missions/${t.mission_id}`}
                    className="px-3 py-1.5 rounded border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 text-xs font-mono flex items-center gap-1"
                  >
                    <Eye className="w-3 h-3" /> Ouvrir
                  </Link>
                  {['DISPATCH_MANDATS', 'FOLLOW_UP_ORGANISM', 'VALIDATE_CLOSURE'].includes(t.action_type) && (
                    <button
                      onClick={() => runExecute(t)}
                      disabled={executing === t.task_id}
                      className="px-3 py-1.5 rounded bg-fuchsia-500/20 border border-fuchsia-500/50 text-fuchsia-400 hover:bg-fuchsia-500/30 text-xs font-mono flex items-center gap-1 disabled:opacity-50"
                    >
                      {executing === t.task_id ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Exécution…</>
                      ) : (
                        <><Bot className="w-3 h-3" /> Traiter (auto)</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rapport dernière exécution */}
      {lastReport && (
        <div className="glass-card border border-fuchsia-500/30 p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-fuchsia-400" />
              <span className="text-fuchsia-400 font-orbitron text-sm font-bold">Dernier rapport agent</span>
            </div>
            <button onClick={() => setLastReport(null)} className="text-gray-500 hover:text-white text-xs">Fermer</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
            <div className="bg-green-500/10 border border-green-500/30 rounded p-2">
              <p className="text-green-400">Emails envoyés</p>
              <p className="text-white text-lg font-bold">{lastReport.sent_email?.length || 0}</p>
            </div>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded p-2">
              <p className="text-orange-400">LRAR préparés</p>
              <p className="text-white text-lg font-bold">{lastReport.prepared_lrar?.length || 0}</p>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2">
              <p className="text-blue-400">Portails manuels</p>
              <p className="text-white text-lg font-bold">{lastReport.manual_portal?.length || 0}</p>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2">
              <p className="text-yellow-400">Non trouvés</p>
              <p className="text-white text-lg font-bold">{lastReport.skipped_no_match?.length || 0}</p>
            </div>
          </div>
          {Array.isArray(lastReport.prepared_lrar) && lastReport.prepared_lrar.length > 0 && (
            <div className="mt-3 text-xs text-gray-400 font-mono">
              <p className="text-orange-400 mb-1">LRAR à poster :</p>
              {lastReport.prepared_lrar.map((l, i) => (
                <p key={i}>• {l.organism} → {l.postal_address || '(adresse inconnue)'}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bandeau SLA */}
      <div className="glass-card border border-gray-800 p-4 text-xs text-gray-500 font-mono">
        SLA : dispatch &lt; {data?.slas?.dispatch_hours || 24}h · relance à {data?.slas?.followup_days || 10}j · validation à {data?.slas?.validation_days || 30}j
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — DisputesPanel : Agent 4 (escalade refus organismes)
// ═══════════════════════════════════════════════════════════════════════
const DisputesPanel = () => {
  const [data, setData] = useState({ pending_escalation: [], overdue_mediation: [] });
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/agent/disputes`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error('Erreur chargement litiges');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const escalate = async (mission_id, organism_id) => {
    if (!window.confirm('Envoyer la mise en demeure ?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/agent/disputes/escalate`, { mission_id, organism_id }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success(`Mise en demeure envoyée (${res.data.channel})`);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const fileMediation = async (mission_id, organism_id) => {
    if (!window.confirm('Marquer la saisine CNPM comme déposée ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/agent/disputes/cnpm-file`, { mission_id, organism_id }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success('Saisine CNPM enregistrée');
      fetch();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  if (loading) return <div className="text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />…</div>;

  return (
    <div className="space-y-6">
      <div className="glass-card border border-red-500/30 p-4">
        <h3 className="font-orbitron text-sm font-bold text-red-400 mb-3">Refus à escalader ({data.pending_escalation.length})</h3>
        {data.pending_escalation.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucun refus en attente.</p>
        ) : (
          <div className="space-y-2">
            {data.pending_escalation.map((d, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-900/50 p-3 rounded">
                <div>
                  <p className="text-white font-mono text-xs">Mission : {d.mission_id?.slice(0, 16)}…</p>
                  <p className="text-gray-500 text-xs">Organisme : {d.organism_id}</p>
                </div>
                <Button onClick={() => escalate(d.mission_id, d.organism_id)} size="sm" className="bg-red-500 text-white hover:bg-red-600">
                  Envoyer mise en demeure
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass-card border border-orange-500/30 p-4">
        <h3 className="font-orbitron text-sm font-bold text-orange-400 mb-3">Médiation CNPM à saisir ({data.overdue_mediation.length})</h3>
        {data.overdue_mediation.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune saisine requise.</p>
        ) : (
          <div className="space-y-2">
            {data.overdue_mediation.map((d, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-900/50 p-3 rounded">
                <div>
                  <p className="text-white font-mono text-xs">Mission : {d.mission_id?.slice(0, 16)}…</p>
                  <p className="text-gray-500 text-xs">Organisme : {d.organism_id} · deadline {d.deadline}</p>
                </div>
                <Button onClick={() => fileMediation(d.mission_id, d.organism_id)} size="sm" className="bg-orange-500 text-white">
                  Saisine déposée
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — AnalyticsPanel : Agent 6 (KPIs hebdo)
// ═══════════════════════════════════════════════════════════════════════
const AnalyticsPanel = () => {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/agent/analytics/weekly`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setM(res.data.metrics);
    } catch (e) {
      toast.error('Erreur analytique');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const sendNow = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/agent/analytics/send-now`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success('Rapport envoyé par email');
    } catch (e) {
      toast.error('Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  if (loading || !m) return <div className="text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />…</div>;

  const kpis = [
    { label: 'CA 7 jours', value: `${(m.revenue?.total || 0).toFixed(2)} €`, delta: `${m.revenue?.delta_percent || 0}% vs n-1` },
    { label: 'Inscriptions', value: m.signups?.count || 0, delta: `${m.signups?.prev_week || 0} la sem. précédente` },
    { label: 'Missions créées', value: m.missions?.created || 0, delta: '' },
    { label: 'Mandats signés', value: m.missions?.mandats_signed || 0, delta: '' },
    { label: 'Missions terminées', value: m.missions?.completed || 0, delta: '' },
    { label: 'Taux conversion', value: `${m.missions?.conversion_rate || 0}%`, delta: 'inscrit → mandat' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-orbitron text-white font-bold">Rapport hebdomadaire</h3>
        <Button onClick={sendNow} disabled={sending} size="sm" className="bg-cyan-500 text-black">
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
          Envoyer maintenant aux admins
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {kpis.map((k, i) => (
          <div key={i} className="glass-card border border-gray-800 p-4">
            <p className="text-gray-500 text-xs font-mono mb-1">{k.label}</p>
            <p className="text-white font-orbitron text-2xl font-black">{k.value}</p>
            {k.delta && <p className="text-gray-400 text-xs mt-1">{k.delta}</p>}
          </div>
        ))}
      </div>
      {m.alerts && m.alerts.length > 0 && (
        <div className="glass-card border border-yellow-500/40 p-4">
          <h4 className="text-yellow-400 font-bold mb-2">Alertes</h4>
          <ul className="space-y-1">
            {m.alerts.map((a, i) => <li key={i} className="text-gray-300 text-sm">⚠️ {a}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — ContentSeoPanel : Agent 5 (génération articles blog)
// ═══════════════════════════════════════════════════════════════════════
const ContentSeoPanel = () => {
  const [articles, setArticles] = useState([]);
  const [topic, setTopic] = useState('');
  const [keyword, setKeyword] = useState('');
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/blog/list?limit=100`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setArticles(res.data.articles || []);
    } catch (e) {
      toast.error('Erreur chargement articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const generate = async () => {
    if (!topic.trim()) { toast.error('Sujet requis'); return; }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/blog/generate`, { topic, keyword: keyword || topic }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success(`Article généré : ${res.data.article.title}`);
      setTopic(''); setKeyword('');
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur génération');
    } finally {
      setGenerating(false);
    }
  };

  const changeStatus = async (article_id, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/blog/${article_id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success(`Statut : ${status}`);
      fetch();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-4">
      <div className="glass-card border border-gray-800 p-4 space-y-3">
        <h3 className="font-orbitron text-sm font-bold text-white">Générer un article</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input placeholder="Sujet (ex: Comment résilier Free)" value={topic} onChange={(e) => setTopic(e.target.value)} className="bg-gray-900 border-gray-700 text-white" />
          <Input placeholder="Mot-clé SEO (optionnel)" value={keyword} onChange={(e) => setKeyword(e.target.value)} className="bg-gray-900 border-gray-700 text-white" />
        </div>
        <Button onClick={generate} disabled={generating} className="bg-cyan-500 text-black">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Génération…</> : <><Sparkles className="w-4 h-4 mr-1" /> Générer via IA</>}
        </Button>
      </div>

      {loading ? (
        <div className="text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />…</div>
      ) : (
        <div className="glass-card border border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/80">
              <tr>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Titre</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Mots</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Statut</th>
                <th className="text-right p-3 text-gray-500 font-mono text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-gray-500">Aucun article généré.</td></tr>
              )}
              {articles.map(a => (
                <tr key={a.article_id} className="border-t border-gray-800/50">
                  <td className="p-3">
                    <p className="text-white">{a.title}</p>
                    <p className="text-gray-500 text-xs font-mono">/{a.slug}</p>
                  </td>
                  <td className="p-3 text-gray-400">{a.word_count}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-mono ${a.status === 'published' ? 'bg-green-500/20 text-green-400' : a.status === 'review' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700 text-gray-400'}`}>{a.status}</span>
                  </td>
                  <td className="p-3 text-right">
                    {a.status !== 'published' && (
                      <Button onClick={() => changeStatus(a.article_id, 'published')} size="sm" className="bg-green-500/20 text-green-400 border border-green-500/50 hover:bg-green-500/30 mr-1">Publier</Button>
                    )}
                    {a.status === 'published' && (
                      <Button onClick={() => changeStatus(a.article_id, 'archived')} size="sm" variant="outline" className="border-gray-700 text-gray-400 mr-1">Archiver</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — ProspectorPanel : Agent 1 (prospection B2B)
// ═══════════════════════════════════════════════════════════════════════
const ProspectorPanel = () => {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, by_segment: {} });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ company_name: '', contact_name: '', email: '', phone: '', segment: 'autre', city: '' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [list, st] = await Promise.all([
        axios.get(`${API}/admin/prospects/list?limit=200`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
        axios.get(`${API}/admin/prospects/stats`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
      ]);
      setProspects(list.data.prospects || []);
      setStats(st.data || { by_status: {}, by_segment: {} });
    } catch (e) {
      toast.error('Erreur chargement prospects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = async () => {
    if (!form.company_name) { toast.error('Entreprise requise'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/prospects/create`, form, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success('Prospect ajouté');
      setShowForm(false);
      setForm({ company_name: '', contact_name: '', email: '', phone: '', segment: 'autre', city: '' });
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const sendEmail = async (p) => {
    if (!p.email) { toast.error('Pas d\'email'); return; }
    if (!window.confirm(`Envoyer email (step ${p.sequence_step || 0}) à ${p.company_name} ?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/prospects/${p.prospect_id}/send-email`, { step: p.sequence_step || 0 }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success(`Envoyé : ${res.data.subject}`);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const updateStatus = async (p, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/prospects/${p.prospect_id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success(`Statut : ${status}`);
      fetch();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const STATUS_COLORS = {
    new: 'bg-gray-700 text-gray-300',
    contacted: 'bg-blue-500/20 text-blue-400',
    followed_up: 'bg-cyan-500/20 text-cyan-400',
    replied: 'bg-yellow-500/20 text-yellow-400',
    meeting: 'bg-purple-500/20 text-purple-400',
    won: 'bg-green-500/20 text-green-400',
    lost: 'bg-red-500/20 text-red-400',
    npai: 'bg-orange-500/20 text-orange-400',
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
        {Object.entries(stats.by_status || {}).map(([s, c]) => (
          <div key={s} className="glass-card border border-gray-800 p-2 text-center">
            <p className="text-gray-500 text-xs font-mono">{s}</p>
            <p className="text-white font-orbitron text-lg font-bold">{c}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button onClick={() => setShowForm(!showForm)} size="sm" className="bg-cyan-500 text-black">
          <UserPlus className="w-4 h-4 mr-1" /> Nouveau prospect
        </Button>
        <Button onClick={fetch} size="sm" variant="outline" className="border-gray-700 text-gray-400">
          <RefreshCw className="w-4 h-4 mr-1" /> Rafraîchir
        </Button>
      </div>

      {showForm && (
        <div className="glass-card border border-cyan-500/40 p-4 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Input placeholder="Entreprise *" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="Contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
            <select value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} className="bg-gray-900 border border-gray-700 text-white rounded px-3 py-2">
              <option value="notaire">Notaire</option>
              <option value="agence_immo">Agence immobilière</option>
              <option value="cgp">CGP</option>
              <option value="comptable">Expert-comptable</option>
              <option value="banque">Banque/Courtier</option>
              <option value="autre">Autre</option>
            </select>
            <Input placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
          </div>
          <Button onClick={create} className="bg-cyan-500 text-black">Ajouter</Button>
        </div>
      )}

      {loading ? (
        <div className="text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />…</div>
      ) : (
        <div className="glass-card border border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/80">
              <tr>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Entreprise</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Contact</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Segment</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Statut</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Séquence</th>
                <th className="text-right p-3 text-gray-500 font-mono text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {prospects.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-500">Aucun prospect encore.</td></tr>
              )}
              {prospects.map(p => (
                <tr key={p.prospect_id} className="border-t border-gray-800/50">
                  <td className="p-3">
                    <p className="text-white">{p.company_name}</p>
                    <p className="text-gray-500 text-xs">{p.city}</p>
                  </td>
                  <td className="p-3 text-gray-400 text-xs">
                    <p>{p.contact_name || '—'}</p>
                    <p className="text-gray-500">{p.email}</p>
                  </td>
                  <td className="p-3 text-gray-400 text-xs">{p.segment}</td>
                  <td className="p-3">
                    <select value={p.status} onChange={(e) => updateStatus(p, e.target.value)} className={`px-2 py-0.5 rounded text-xs font-mono border-0 ${STATUS_COLORS[p.status] || 'bg-gray-700 text-gray-300'}`}>
                      {['new', 'contacted', 'followed_up', 'replied', 'meeting', 'won', 'lost', 'npai'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-gray-400 text-xs">Étape {p.sequence_step || 0}/3</td>
                  <td className="p-3 text-right">
                    <Button onClick={() => sendEmail(p)} size="sm" className="bg-fuchsia-500/20 text-fuchsia-400 border border-fuchsia-500/50 hover:bg-fuchsia-500/30">
                      <Send className="w-3 h-3 mr-1" /> Envoyer
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — ChatbotEscalationsPanel : rappels clients quand le bot ne sait pas répondre
// ═══════════════════════════════════════════════════════════════════════
const ChatbotEscalationsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/chatbot/admin/escalations?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setItems(res.data.escalations || []);
    } catch (e) {
      toast.error('Erreur chargement rappels');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  const resolve = async (id) => {
    if (!window.confirm('Marquer ce rappel comme traité ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/chatbot/admin/escalations/${id}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success('Rappel marqué résolu');
      fetch();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h3 className="font-orbitron text-white font-bold">Demandes de rappel client ({items.length})</h3>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-gray-900 border border-gray-700 text-white text-sm rounded px-3 py-1 font-mono">
          <option value="pending">En attente</option>
          <option value="resolved">Résolus</option>
          <option value="">Tous</option>
        </select>
      </div>

      {loading ? <div className="text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />…</div> : (
        items.length === 0 ? (
          <div className="glass-card border border-gray-800 p-10 text-center">
            <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
            <p className="text-gray-300 font-mono">Aucune demande de rappel en attente.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(e => (
              <div key={e.escalation_id} className="glass-card border border-red-500/30 p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Bell className="w-4 h-4 text-red-400" />
                      <span className="text-white font-semibold">{e.user_first_name || e.user_email}</span>
                      <span className="text-gray-500 text-xs font-mono">{new Date(e.created_at).toLocaleString('fr-FR')}</span>
                    </div>
                    <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-3 mb-2">
                      <p className="text-xs text-yellow-400 font-mono mb-1">QUESTION CLIENT :</p>
                      <p className="text-white text-sm">{e.question}</p>
                    </div>
                    <div className="bg-gray-900/50 p-3 text-xs text-gray-400 font-mono space-y-1">
                      <p>📞 Téléphone : <span className={e.user_phone ? 'text-cyan-400' : 'text-red-400'}>{e.user_phone || 'NON RENSEIGNÉ'}</span></p>
                      <p>📧 Email : <span className="text-cyan-400">{e.user_email}</span></p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {e.user_phone && (
                      <a href={`tel:${e.user_phone}`} className="px-3 py-1.5 rounded bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 text-xs font-mono flex items-center gap-1 hover:bg-cyan-500/30">
                        📞 Rappeler
                      </a>
                    )}
                    {e.user_email && (
                      <a href={`mailto:${e.user_email}?subject=Votre question L'EFFACEUR`} className="px-3 py-1.5 rounded bg-magenta-500/20 border border-magenta-500/50 text-fuchsia-400 text-xs font-mono flex items-center gap-1 hover:bg-magenta-500/30">
                        ✉️ Email
                      </a>
                    )}
                    {e.status === 'pending' && (
                      <button onClick={() => resolve(e.escalation_id)} className="px-3 py-1.5 rounded bg-green-500/20 border border-green-500/50 text-green-400 text-xs font-mono hover:bg-green-500/30">
                        ✓ Résolu
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — AccountsStatusPanel : liste des services à brancher (DocuSign, Sentry, etc.)
// ═══════════════════════════════════════════════════════════════════════
const AccountsStatusPanel = () => {
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/signatures/status`, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true
        });
        setStatuses({ docusign: res.data.docusign_configured });
      } catch (e) { /* silencieux */ }
      finally { setLoading(false); }
    })();
  }, []);

  const SERVICES = [
    { key: 'render', name: 'Render (hébergement backend 24/7)', url: 'https://render.com', price: '7 €/mois', priority: 1, impact: 'Site disponible en permanence même PC éteint' },
    { key: 'vercel', name: 'Vercel (hébergement frontend)', url: 'https://vercel.com', price: 'Gratuit', priority: 1, impact: 'Domaine public effaceurpro.fr' },
    { key: 'sentry', name: 'Sentry (monitoring erreurs)', url: 'https://sentry.io', price: 'Gratuit (dev)', priority: 2, impact: 'Alerte si backend crash' },
    { key: 'uptimerobot', name: 'UptimeRobot (ping)', url: 'https://uptimerobot.com', price: 'Gratuit', priority: 2, impact: 'SMS/Email si site offline' },
    { key: 'plausible', name: 'Plausible Analytics', url: 'https://plausible.io', price: '9 €/mois', priority: 3, impact: 'Savoir d\'où viennent les visiteurs' },
    { key: 'brevo_inbound', name: 'Brevo Inbound Parsing', url: 'https://app.brevo.com', price: 'Inclus Brevo', priority: 2, impact: 'Réponses organismes captées automatiquement' },
    { key: 'brevo_sms', name: 'Brevo SMS', url: 'https://app.brevo.com', price: '0.07 €/SMS', priority: 3, impact: 'Notifs SMS clients' },
    { key: 'ar24', name: 'AR24 (Lettre Recommandée Électronique)', url: 'https://ar24.fr', price: '3 €/LRE', priority: 2, impact: 'LRE auto pour organismes LRAR-only' },
    { key: 'docusign', name: 'DocuSign (signature CGU Pros)', url: 'https://docusign.com', price: '23 €/mois', priority: 3, impact: 'Signature qualifiée pour partenaires B2B', connected: statuses.docusign },
    { key: 'turnstile', name: 'Cloudflare Turnstile (anti-spam)', url: 'https://cloudflare.com/products/turnstile', price: 'Gratuit', priority: 2, impact: 'Protection signup/login' },
    { key: 'apify', name: 'Apify (scraping prospects)', url: 'https://apify.com', price: '50 €/mois', priority: 4, impact: 'Enrichissement Agent Prospecteur' },
    { key: 'instantly', name: 'Instantly (séquences emails prospection)', url: 'https://instantly.ai', price: '37 €/mois', priority: 4, impact: 'Volume élevé prospection' },
    { key: 'bridge', name: 'Bridge API (DSP2 bancaire)', url: 'https://bridgeapi.io', price: '0.50 €/connexion', priority: 4, impact: 'Lecture bancaire auto, fin du CSV' },
  ];

  if (loading) return <div className="text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />…</div>;

  return (
    <div className="space-y-4">
      <div className="glass-card border border-gray-800 p-4">
        <h3 className="font-orbitron text-white font-bold mb-2">Comptes à brancher</h3>
        <p className="text-gray-500 text-sm">Services externes pour étendre les capacités de L'EFFACEUR. Priorité 1 = indispensable, 4 = plus tard.</p>
      </div>

      <div className="space-y-2">
        {SERVICES.sort((a, b) => a.priority - b.priority).map(s => (
          <div key={s.key} className="glass-card border border-gray-800 p-4 flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold ${s.priority === 1 ? 'bg-red-500/20 text-red-400' : s.priority === 2 ? 'bg-orange-500/20 text-orange-400' : s.priority === 3 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700 text-gray-400'}`}>P{s.priority}</span>
                <span className="text-white font-semibold">{s.name}</span>
                {s.connected !== undefined && (
                  <span className={`px-2 py-0.5 rounded text-xs font-mono ${s.connected ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                    {s.connected ? '● CONNECTÉ' : '○ NON CONNECTÉ'}
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-xs">{s.impact}</p>
            </div>
            <div className="text-right">
              <p className="text-cyan-400 font-mono text-sm">{s.price}</p>
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-cyan-400 font-mono">
                Créer compte →
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — AgentSuperPanel : wrapper avec sous-onglets (File / Catalogue / Scheduler / Historique)
// ═══════════════════════════════════════════════════════════════════════
const AgentSuperPanel = () => {
  const [subTab, setSubTab] = useState('queue');
  const subTabs = [
    { id: 'queue', label: 'File d\'attente', icon: Bot },
    { id: 'catalog', label: 'Catalogue', icon: Briefcase },
    { id: 'scheduler', label: 'Automatisation', icon: Clock },
    { id: 'disputes', label: 'Litiges', icon: AlertTriangle },
    { id: 'analytics', label: 'Analytique', icon: BarChart3 },
    { id: 'content', label: 'Content SEO', icon: FileText },
    { id: 'prospects', label: 'Prospection B2B', icon: UserPlus },
    { id: 'chatbot', label: 'Rappels clients', icon: Bell },
    { id: 'accounts', label: 'Comptes à brancher', icon: ExternalLink },
    { id: 'runs', label: 'Historique', icon: Activity },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap border-b border-gray-800 pb-2">
        {subTabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              className={`px-4 py-2 rounded-t font-mono text-xs transition-all flex items-center gap-2 ${subTab === t.id ? 'bg-fuchsia-500/20 text-fuchsia-400 border border-fuchsia-500/40' : 'text-gray-500 hover:text-white'}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {subTab === 'queue' && <AgentQueuePanel />}
      {subTab === 'catalog' && <OrganismCatalogPanel />}
      {subTab === 'scheduler' && <AgentSchedulerPanel />}
      {subTab === 'disputes' && <DisputesPanel />}
      {subTab === 'analytics' && <AnalyticsPanel />}
      {subTab === 'content' && <ContentSeoPanel />}
      {subTab === 'prospects' && <ProspectorPanel />}
      {subTab === 'chatbot' && <ChatbotEscalationsPanel />}
      {subTab === 'accounts' && <AccountsStatusPanel />}
      {subTab === 'runs' && <AgentRunsPanel />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — AgentRunsPanel : historique des exécutions
// ═══════════════════════════════════════════════════════════════════════
const AgentRunsPanel = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/agent/runs?limit=100`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
        setRuns(res.data.runs || []);
      } catch (e) {
        toast.error('Erreur chargement historique');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />…</div>;

  return (
    <div className="glass-card border border-gray-800 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-900/80">
          <tr>
            <th className="text-left p-3 text-gray-500 font-mono text-xs">Quand</th>
            <th className="text-left p-3 text-gray-500 font-mono text-xs">Type</th>
            <th className="text-left p-3 text-gray-500 font-mono text-xs">Mission</th>
            <th className="text-left p-3 text-gray-500 font-mono text-xs">Auteur</th>
            <th className="text-left p-3 text-gray-500 font-mono text-xs">Résultat</th>
          </tr>
        </thead>
        <tbody>
          {runs.length === 0 && (
            <tr><td colSpan={5} className="p-6 text-center text-gray-500">Aucune exécution enregistrée.</td></tr>
          )}
          {runs.map((r, i) => (
            <tr key={i} className="border-t border-gray-800/50">
              <td className="p-3 text-gray-400 font-mono text-xs">{r.executed_at ? new Date(r.executed_at).toLocaleString('fr-FR') : '—'}</td>
              <td className="p-3">
                <span className="px-2 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-400 font-mono text-xs">{r.run_type}</span>
              </td>
              <td className="p-3 text-cyan-400 font-mono text-xs">{r.mission_id ? r.mission_id.slice(0, 16) + '…' : '—'}</td>
              <td className="p-3 text-gray-500 text-xs">{r.agent_email || 'system'}</td>
              <td className="p-3 text-xs font-mono text-gray-400">
                {r.report?.sent_email_count !== undefined && <span>{r.report.sent_email_count} email(s) </span>}
                {r.report?.prepared_lrar_count !== undefined && <span>· {r.report.prepared_lrar_count} LRAR </span>}
                {r.report?.relances_count !== undefined && <span>{r.report.relances_count} relance(s)</span>}
                {r.report?.new_status && <span className="text-green-400">→ {r.report.new_status}</span>}
                {r.report?.missions_processed !== undefined && <span>{r.report.missions_processed} missions (scheduler)</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.4 — AgentPerformancePanel : metrics + alertes + supervisor (agents évolutifs)
// ═══════════════════════════════════════════════════════════════════════
const AgentPerformancePanel = () => {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const [statsRes, alertsRes, failuresRes] = await Promise.all([
        axios.get(`${API}/agent-metrics/stats?days=7`, cfg).catch(() => ({ data: { agents: [] } })),
        axios.get(`${API}/agent-metrics/alerts?days=7`, cfg).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API}/agent-metrics/failures?limit=15`, cfg).catch(() => ({ data: { failures: [] } })),
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setFailures(failuresRes.data.failures || []);
    } catch (e) {
      toast.error('Erreur chargement métriques');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runSupervisor = async () => {
    setRunning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/agent-metrics/run-supervisor`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Supervisor : ${res.data.alerts_count || 0} alerte(s) (${res.data.critical_count || 0} critiques)`);
      fetchAll();
    } catch (e) {
      toast.error('Erreur supervisor');
    } finally {
      setRunning(false);
    }
  };

  const runEvaluator = async () => {
    setRunning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/agent-metrics/run-evaluator`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Auto-évaluateur : ${res.data.evaluated || 0}/${res.data.checked || 0} runs évalués`);
      fetchAll();
    } catch (e) {
      toast.error('Erreur evaluator');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-fuchsia-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(255,0,255,0.8)' }} />
          <h2 className="font-orbitron text-xl font-black text-white">Performance Agents IA</h2>
          <span className="font-mono text-xs text-fuchsia-400/60">// METRICS · AUTO-EVAL · SUPERVISOR</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={runEvaluator} disabled={running} variant="outline" className="text-xs">
            {running ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
            Lancer auto-évaluateur
          </Button>
          <Button onClick={runSupervisor} disabled={running} className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-xs">
            {running ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
            Lancer supervisor
          </Button>
          <Button onClick={fetchAll} variant="outline" className="text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* === ALERTES === */}
      {alerts.length > 0 && (
        <div className="glass-card border border-red-500/40 p-5">
          <h3 className="font-orbitron text-sm font-bold text-white mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            Alertes actives ({alerts.length})
          </h3>
          <div className="space-y-2">
            {alerts.map((a, idx) => (
              <div key={idx} className={`p-3 rounded border ${a.severity === 'critical' ? 'border-red-500/40 bg-red-500/10' : 'border-orange-500/40 bg-orange-500/10'}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${a.severity === 'critical' ? 'bg-red-500/20 text-red-400 border-red-500/40' : 'bg-orange-500/20 text-orange-400 border-orange-500/40'}`}>
                        {a.severity?.toUpperCase()}
                      </span>
                      <span className="text-sm font-bold text-white">{a.agent}.{a.action}</span>
                      <span className="text-xs text-gray-500 font-mono">{a.type}</span>
                    </div>
                    <div className="text-xs text-gray-300">{a.details}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === STATS PAR AGENT === */}
      <div className="glass-card border border-fuchsia-500/20 overflow-hidden">
        <div className="bg-gray-900/80 p-4 border-b border-gray-800 flex items-center gap-3">
          <Gauge className="w-5 h-5 text-fuchsia-400" />
          <h3 className="font-orbitron text-sm font-bold text-white">Performance par agent — 7 jours</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-fuchsia-400" /></div>
        ) : !stats?.agents?.length ? (
          <div className="p-8 text-center text-gray-500">
            Aucune donnée encore. Les métriques apparaîtront après les premiers runs des agents.
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {stats.agents.map((agent) => (
              <div key={agent.agent} className="p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="font-bold text-white">{agent.agent}</div>
                    <div className="text-xs text-gray-500">{agent.total_runs} runs · {agent.total_successes} succès · {agent.total_failures} échecs</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-black ${agent.success_rate >= 0.9 ? 'text-green-400' : agent.success_rate >= 0.7 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {(agent.success_rate * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-gray-500">taux succès</div>
                  </div>
                </div>
                <div className="space-y-1.5 mt-2">
                  {agent.actions?.map((a) => (
                    <div key={a.action} className="flex items-center gap-3 text-xs">
                      <div className="font-mono text-gray-400 w-32 truncate">{a.action}</div>
                      <div className="flex-1 bg-gray-900/50 rounded h-1.5 overflow-hidden">
                        <div className={`h-full ${a.success_rate >= 0.9 ? 'bg-green-400' : a.success_rate >= 0.7 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{ width: `${a.success_rate * 100}%` }} />
                      </div>
                      <div className="text-gray-300 w-20 text-right">{(a.success_rate * 100).toFixed(0)}% · {a.total} runs</div>
                      {a.avg_eval_score !== null && a.avg_eval_score !== undefined && (
                        <div className={`w-16 text-right text-xs ${a.avg_eval_score >= 0.7 ? 'text-green-400' : 'text-orange-400'}`}>
                          eval {a.avg_eval_score.toFixed(2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* === ÉCHECS RÉCENTS === */}
      {failures.length > 0 && (
        <div className="glass-card border border-orange-500/20 overflow-hidden">
          <div className="bg-gray-900/80 p-4 border-b border-gray-800 flex items-center gap-3">
            <TrendingDown className="w-5 h-5 text-orange-400" />
            <h3 className="font-orbitron text-sm font-bold text-white">Échecs récents</h3>
          </div>
          <div className="divide-y divide-gray-800 max-h-96 overflow-y-auto">
            {failures.map((f, idx) => (
              <div key={idx} className="p-3 hover:bg-gray-900/30">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/30">FAIL</span>
                  <span className="text-sm font-bold text-white">{f.agent}.{f.action}</span>
                  <span className="text-[10px] text-gray-500 font-mono ml-auto">{new Date(f.timestamp).toLocaleString('fr-FR')}</span>
                </div>
                {f.error && <div className="text-xs text-red-400 mt-1 italic truncate">{f.error}</div>}
                {f.context && Object.keys(f.context).length > 0 && (
                  <div className="text-xs text-gray-500 font-mono mt-1 truncate">{JSON.stringify(f.context).slice(0, 120)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.4 — InboxManagerPanel : Gmail OAuth + emails classifiés + brouillons IA
// ═══════════════════════════════════════════════════════════════════════
const CATEGORY_BADGE = {
  urgent: { label: 'URGENT', class: 'bg-red-500/20 text-red-400 border-red-500/40' },
  client: { label: 'CLIENT', class: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40' },
  partenaire: { label: 'PARTENAIRE', class: 'bg-purple-500/20 text-purple-400 border-purple-500/40' },
  organisme: { label: 'ORGANISME', class: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  newsletter: { label: 'NEWSLETTER', class: 'bg-gray-500/20 text-gray-400 border-gray-500/40' },
  spam: { label: 'SPAM', class: 'bg-orange-500/20 text-orange-400 border-orange-500/40' },
  perso: { label: 'PERSO', class: 'bg-green-500/20 text-green-400 border-green-500/40' },
};

const InboxManagerPanel = () => {
  const [accounts, setAccounts] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [summaries, setSummaries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const [statusRes, statsRes, summariesRes] = await Promise.all([
        axios.get(`${API}/auth/google/status`, cfg),
        axios.get(`${API}/inbox/stats`, cfg).catch(() => ({ data: null })),
        axios.get(`${API}/inbox/summaries?limit=80${filter ? `&category=${filter}` : ''}`, cfg).catch(() => ({ data: { summaries: [] } })),
      ]);
      setConfigured(statusRes.data.configured);
      setAccounts(statusRes.data.accounts || []);
      setStats(statsRes.data);
      setSummaries(summariesRes.data.summaries || []);
    } catch (e) {
      toast.error('Erreur chargement Email');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Détecte le retour OAuth (?gmail=connected)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected') {
      const email = params.get('email');
      toast.success(`Compte ${email} connecté !`);
      window.history.replaceState({}, '', '/admin');
      fetchAll();
    } else if (params.get('gmail') === 'error') {
      const reason = params.get('reason') || 'inconnu';
      toast.error(`Connexion Gmail échouée : ${reason}`);
      window.history.replaceState({}, '', '/admin');
    }
  }, [fetchAll]);

  const handleConnect = async (label) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/auth/google/prepare-connect`, { account: label }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        toast.error("URL OAuth non reçue");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur préparation OAuth";
      toast.error(msg);
    }
  };

  const handleDisconnect = async (label) => {
    if (!window.confirm(`Déconnecter le compte "${label}" ?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/google/disconnect?label=${label}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Compte ${label} déconnecté`);
      fetchAll();
    } catch (e) {
      toast.error('Échec déconnexion');
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/inbox/run-now`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data || {};
      // Statuts possibles : ok, partial, no_accounts, config_error, import_error, fatal_error
      if (d.status === 'config_error' || d.status === 'import_error' || d.status === 'fatal_error') {
        toast.error(`Email — ${d.status} : ${d.error || 'erreur inconnue'}`, { duration: 8000 });
      } else if (d.status === 'no_accounts') {
        toast.error('Aucun compte Gmail connecté');
      } else if (d.status === 'partial' && d.errors?.length) {
        const errMsg = d.errors.map(e => `${e.email}: ${e.error}`).join(' · ');
        toast.warning(`Partiellement OK (${d.total_processed || 0} traités) — ${errMsg}`, { duration: 10000 });
      } else {
        toast.success(`Email terminé : ${d.total_processed || 0} email(s) traité(s), ${d.total_drafted || 0} brouillon(s) créé(s)`);
      }
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Échec exécution : ${detail}`, { duration: 8000 });
    } finally {
      setRunning(false);
    }
  };

  const handleSendDraft = async (msgId, label) => {
    if (!window.confirm('Envoyer ce brouillon maintenant ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/inbox/send-draft`, { msg_id: msgId, label }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Email envoyé');
      fetchAll();
    } catch (e) {
      toast.error('Échec envoi');
    }
  };

  const handleDeleteDraft = async (msgId, label) => {
    if (!window.confirm('Supprimer ce brouillon ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/inbox/draft/${msgId}?label=${label}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Brouillon supprimé');
      fetchAll();
    } catch (e) {
      toast.error('Échec suppression');
    }
  };

  if (!configured) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-1 h-8 bg-cyan-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(0,229,204,0.8)' }} />
          <h2 className="font-orbitron text-xl font-black text-white">Email</h2>
          <span className="font-mono text-xs text-cyan-400/60">// GMAIL · OAUTH</span>
        </div>
        <div className="glass-card border border-orange-500/30 p-6">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            <h3 className="font-orbitron text-sm font-bold text-white">OAuth Google non configuré</h3>
          </div>
          <p className="text-sm text-gray-400 mb-3">
            Les variables d'environnement <code className="text-cyan-400">GOOGLE_CLIENT_ID</code> et <code className="text-cyan-400">GOOGLE_CLIENT_SECRET</code> sont manquantes côté Render.
          </p>
          <p className="text-xs text-gray-500">Une fois ajoutées, redéploie le backend et reviens sur cette page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-cyan-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(0,229,204,0.8)' }} />
          <h2 className="font-orbitron text-xl font-black text-white">Email</h2>
          <span className="font-mono text-xs text-cyan-400/60">// GMAIL · CLASSIFICATION · DRAFTS</span>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRunNow} disabled={running || accounts.length === 0} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold text-xs">
            {running ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
            Lancer maintenant
          </Button>
          <Button onClick={fetchAll} variant="outline" className="text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* === COMPTES CONNECTÉS === */}
      <div className="glass-card border border-cyan-500/20 p-5">
        <h3 className="font-orbitron text-sm font-bold text-white mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4 text-cyan-400" />
          Comptes Gmail connectés
        </h3>
        {accounts.length === 0 ? (
          <p className="text-sm text-gray-400 mb-4">Aucun compte connecté. Connecte ton Gmail perso et/ou pro pour que l'IA classe et drafte tes emails automatiquement.</p>
        ) : (
          <div className="space-y-2 mb-4">
            {accounts.map(a => (
              <div key={a.label} className="flex items-center justify-between bg-gray-900/50 border border-gray-800 rounded p-3">
                <div>
                  <div className="text-sm font-bold text-white">{a.email}</div>
                  <div className="text-xs text-gray-500">
                    Label : <span className="text-cyan-400 font-mono">{a.label}</span>
                    {a.last_sync && <span className="ml-3">Dernière sync : {new Date(a.last_sync).toLocaleString('fr-FR')}</span>}
                  </div>
                </div>
                <Button onClick={() => handleDisconnect(a.label)} variant="outline" className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-3 h-3 mr-1" /> Déconnecter
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          {!accounts.find(a => a.label === 'perso') && (
            <Button onClick={() => handleConnect('perso')} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold text-xs">
              <Plus className="w-3 h-3 mr-1" /> Connecter Gmail perso
            </Button>
          )}
          {!accounts.find(a => a.label === 'pro') && (
            <Button onClick={() => handleConnect('pro')} className="bg-purple-500 hover:bg-purple-600 text-white font-bold text-xs">
              <Plus className="w-3 h-3 mr-1" /> Connecter Gmail pro
            </Button>
          )}
        </div>
      </div>

      {/* === STATS 7 JOURS === */}
      {stats && (
        <div className="glass-card border border-cyan-500/20 p-5">
          <h3 className="font-orbitron text-sm font-bold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            Activité IA — 7 jours
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-900/50 border border-gray-800 rounded p-3">
              <div className="text-xs text-gray-500 font-mono">TOTAL TRAITÉS</div>
              <div className="text-2xl font-black text-cyan-400">{stats.total_processed}</div>
            </div>
            {Object.entries(stats.by_category || {}).map(([cat, val]) => (
              <div key={cat} className="bg-gray-900/50 border border-gray-800 rounded p-3">
                <div className="text-xs text-gray-500 font-mono">{(CATEGORY_BADGE[cat]?.label) || cat.toUpperCase()}</div>
                <div className="text-2xl font-black text-white">{val.count}</div>
                {val.drafted > 0 && <div className="text-xs text-cyan-400">{val.drafted} brouillon(s)</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === FILTRE === */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter('')} className={`text-xs px-3 py-1 rounded font-mono ${!filter ? 'bg-cyan-500 text-black' : 'bg-gray-800 text-gray-400'}`}>
          TOUT
        </button>
        {Object.entries(CATEGORY_BADGE).map(([key, b]) => (
          <button key={key} onClick={() => setFilter(key)} className={`text-xs px-3 py-1 rounded font-mono border ${filter === key ? b.class : 'bg-gray-800 text-gray-500 border-gray-700'}`}>
            {b.label}
          </button>
        ))}
      </div>

      {/* === LISTE EMAILS TRAITÉS === */}
      <div className="glass-card border border-cyan-500/20 overflow-hidden">
        <div className="bg-gray-900/80 p-4 border-b border-gray-800 flex items-center gap-3">
          <MailCheck className="w-5 h-5 text-cyan-400" />
          <h3 className="font-orbitron text-sm font-bold text-white">Emails traités ({summaries.length})</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : summaries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {accounts.length === 0 ? 'Connecte d\'abord un compte Gmail.' : 'Aucun email traité encore. Clique "Lancer maintenant" pour démarrer le pipeline.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {summaries.map((s) => {
              const cat = s.classification?.category || 'perso';
              const badge = CATEGORY_BADGE[cat] || CATEGORY_BADGE.perso;
              const expanded = expandedId === s.msg_id;
              const hasDraft = !!s.draft?.draft_id;
              const sent = s.actions?.sent;
              return (
                <div key={s.msg_id} className="p-3 hover:bg-gray-900/30 transition-colors">
                  <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setExpandedId(expanded ? null : s.msg_id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${badge.class}`}>{badge.label}</span>
                        {s.classification?.priority && <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-gray-800 text-gray-400 border-gray-700">{s.classification.priority}</span>}
                        {hasDraft && !sent && <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-cyan-500/20 text-cyan-400 border-cyan-500/40">BROUILLON IA</span>}
                        {sent && <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-green-500/20 text-green-400 border-green-500/40">ENVOYÉ</span>}
                        <span className="text-xs text-gray-500 font-mono">{s.label}</span>
                      </div>
                      <div className="text-sm font-bold text-white truncate">{s.subject}</div>
                      <div className="text-xs text-gray-500 truncate">{s.from}</div>
                      <div className="text-xs text-gray-400 mt-1 italic">{s.classification?.summary}</div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </div>
                  {expanded && (
                    <div className="mt-3 ml-2 pl-3 border-l-2 border-cyan-500/30 space-y-3">
                      <div>
                        <div className="text-[10px] text-gray-500 font-mono uppercase mb-1">Snippet</div>
                        <div className="text-xs text-gray-300">{s.snippet}</div>
                      </div>
                      {s.classification?.intent && (
                        <div>
                          <div className="text-[10px] text-gray-500 font-mono uppercase mb-1">Intent IA</div>
                          <div className="text-xs text-cyan-400">{s.classification.intent}</div>
                        </div>
                      )}
                      {hasDraft && !sent && (
                        <div className="bg-gray-900/50 border border-cyan-500/30 rounded p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] text-cyan-400 font-mono uppercase">
                              Brouillon IA · Confiance {Math.round((s.draft?.confidence || 0) * 100)}%
                            </div>
                            {s.draft?.warnings?.length > 0 && (
                              <span className="text-[10px] text-orange-400 font-mono">⚠ {s.draft.warnings.length} avertissement(s)</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-300 mb-3 italic">
                            Ouvrir le brouillon dans Gmail pour le voir, l'éditer et l'envoyer manuellement (recommandé).
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleSendDraft(s.msg_id, s.label)} className="bg-green-500 hover:bg-green-600 text-black font-bold text-xs">
                              <Send className="w-3 h-3 mr-1" /> Envoyer tel quel
                            </Button>
                            <Button onClick={() => handleDeleteDraft(s.msg_id, s.label)} variant="outline" className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10">
                              <Trash2 className="w-3 h-3 mr-1" /> Supprimer brouillon
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.3 — FinancePanel : charges, revenus, seuil rentabilité, burn rate
// ═══════════════════════════════════════════════════════════════════════
const STATUS_COLORS_FINANCE = {
  active: 'bg-green-500/20 text-green-400 border-green-500/40',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  inactive: 'bg-gray-700 text-gray-400 border-gray-700',
};

const FinancePanel = () => {
  const [data, setData] = useState({ charges: [], totals: {} });
  const [summary, setSummary] = useState(null);
  const [revenue, setRevenue] = useState({ series: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ service: '', description: '', monthly_amount: 0, status: 'pending', priority: 'P2', category: 'hosting', notes: '' });
  const [editingId, setEditingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [c, s, r] = await Promise.all([
        axios.get(`${API}/admin/finance/charges`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
        axios.get(`${API}/admin/finance/summary`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
        axios.get(`${API}/admin/finance/revenue/monthly?months=6`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
      ]);
      setData(c.data);
      setSummary(s.data);
      setRevenue(r.data);
    } catch (e) {
      toast.error('Erreur chargement Finance');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    if (!form.service) { toast.error('Service obligatoire'); return; }
    try {
      const token = localStorage.getItem('token');
      if (editingId) {
        await axios.patch(`${API}/admin/finance/charges/${editingId}`, form, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      } else {
        await axios.post(`${API}/admin/finance/charges`, form, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      }
      toast.success('Charge enregistrée');
      setShowForm(false);
      setEditingId(null);
      setForm({ service: '', description: '', monthly_amount: 0, status: 'pending', priority: 'P2', category: 'hosting', notes: '' });
      fetchAll();
    } catch (e) {
      toast.error('Erreur sauvegarde');
    }
  };

  const edit = (c) => {
    setForm({
      service: c.service, description: c.description || '', monthly_amount: c.monthly_amount,
      status: c.status, priority: c.priority, category: c.category, notes: c.notes || '',
    });
    setEditingId(c.charge_id);
    setShowForm(true);
  };

  const toggleStatus = async (c) => {
    const next = c.status === 'active' ? 'pending' : 'active';
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/finance/charges/${c.charge_id}`, { status: next }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
  };

  const removeCharge = async (id) => {
    if (!window.confirm('Supprimer cette charge ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/finance/charges/${id}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />…</div>;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="glass-card border border-red-500/30 p-4">
            <p className="text-gray-400 text-xs font-mono mb-1">Charges actives / mois</p>
            <p className="text-white font-orbitron text-2xl font-black">{summary.monthly_charges.toFixed(0)} €</p>
            <p className="text-gray-500 text-xs mt-1">{summary.annual_charges.toFixed(0)} €/an</p>
          </div>
          <div className="glass-card border border-green-500/30 p-4">
            <p className="text-gray-400 text-xs font-mono mb-1">CA mois courant</p>
            <p className="text-white font-orbitron text-2xl font-black">{summary.monthly_revenue.toFixed(0)} €</p>
            <p className="text-gray-500 text-xs mt-1">{summary.monthly_sales_count} vente(s)</p>
          </div>
          <div className={`glass-card border p-4 ${summary.monthly_net >= 0 ? 'border-green-500/30' : 'border-red-500/30'}`}>
            <p className="text-gray-400 text-xs font-mono mb-1">Résultat net</p>
            <p className={`font-orbitron text-2xl font-black ${summary.monthly_net >= 0 ? 'text-green-400' : 'text-red-400'}`}>{summary.monthly_net.toFixed(0)} €</p>
            <p className="text-gray-500 text-xs mt-1">{summary.monthly_net >= 0 ? '✅ rentable' : '⚠️ déficit'}</p>
          </div>
          <div className="glass-card border border-cyan-500/30 p-4">
            <p className="text-gray-400 text-xs font-mono mb-1">Seuil rentabilité</p>
            <p className="text-white font-orbitron text-2xl font-black">{summary.break_even_clients_demenagement}</p>
            <p className="text-gray-500 text-xs mt-1">clients Déménagement/mois</p>
          </div>
        </div>
      )}

      {/* Revenus 6 derniers mois */}
      {revenue.series && revenue.series.length > 0 && (
        <div className="glass-card border border-gray-800 p-4">
          <h3 className="font-orbitron text-sm font-bold text-white mb-3">CA par mois (6 derniers)</h3>
          <div className="flex items-end gap-2 h-32">
            {revenue.series.map((m, i) => {
              const maxRev = Math.max(...revenue.series.map(x => x.revenue), 1);
              const height = (m.revenue / maxRev) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end">
                  <span className="text-xs text-cyan-400 font-mono mb-1">{m.revenue.toFixed(0)}€</span>
                  <div className="w-full bg-cyan-500/20 border-t-2 border-cyan-500" style={{ height: `${height}%`, minHeight: '4px' }} />
                  <span className="text-xs text-gray-500 font-mono mt-1">{m.month.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ service: '', description: '', monthly_amount: 0, status: 'pending', priority: 'P2', category: 'hosting', notes: '' }); }} size="sm" className="bg-cyan-500 text-black">
          <Plus className="w-4 h-4 mr-1" /> Ajouter charge
        </Button>
        <Button onClick={fetchAll} size="sm" variant="outline" className="border-gray-700 text-gray-400">
          <RefreshCw className="w-4 h-4 mr-1" /> Rafraîchir
        </Button>
        <Button onClick={async () => {
          if (!window.confirm('Supprimer les charges en doublon ?')) return;
          try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API}/admin/finance/dedupe`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`${res.data.deleted} doublon(s) supprimé(s)`);
            fetchAll();
          } catch (e) {
            toast.error('Échec dédoublonnage');
          }
        }} size="sm" variant="outline" className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10">
          <Trash2 className="w-4 h-4 mr-1" /> Dédoublonner
        </Button>
      </div>

      {/* Formulaire ajout/édition */}
      {showForm && (
        <div className="glass-card border border-cyan-500/40 p-4 space-y-2">
          <h3 className="text-cyan-400 font-orbitron text-sm font-bold">{editingId ? 'Modifier la charge' : 'Nouvelle charge'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Input placeholder="Service (ex: Notion)" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="bg-gray-900 border-gray-700 text-white" />
            <Input type="number" placeholder="Montant €/mois" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: parseFloat(e.target.value) || 0 })} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-gray-900 border-gray-700 text-white md:col-span-2" />
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="bg-gray-900 border border-gray-700 text-white rounded px-3 py-2">
              <option value="active">Actif</option>
              <option value="pending">À activer</option>
              <option value="inactive">Inactif</option>
            </select>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="bg-gray-900 border border-gray-700 text-white rounded px-3 py-2">
              <option value="P1">P1 Critique</option>
              <option value="P2">P2 Important</option>
              <option value="P3">P3 Moyen</option>
              <option value="P4">P4 Plus tard</option>
            </select>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-gray-900 border border-gray-700 text-white rounded px-3 py-2 md:col-span-2">
              <option value="hosting">Hébergement / Infra</option>
              <option value="ai">IA / Dev</option>
              <option value="marketing">Marketing</option>
              <option value="legal">Légal / Assurances</option>
              <option value="other">Autre</option>
            </select>
            <Input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-gray-900 border-gray-700 text-white md:col-span-2" />
          </div>
          <div className="flex gap-2">
            <Button onClick={save} className="bg-cyan-500 text-black">Enregistrer</Button>
            <Button onClick={() => { setShowForm(false); setEditingId(null); }} variant="outline" className="border-gray-700 text-gray-400">Annuler</Button>
          </div>
        </div>
      )}

      {/* Tableau charges */}
      <div className="glass-card border border-gray-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-900/80">
            <tr>
              <th className="text-left p-3 text-gray-500 font-mono text-xs">Service</th>
              <th className="text-left p-3 text-gray-500 font-mono text-xs">Description</th>
              <th className="text-right p-3 text-gray-500 font-mono text-xs">€/mois</th>
              <th className="text-left p-3 text-gray-500 font-mono text-xs">Statut</th>
              <th className="text-left p-3 text-gray-500 font-mono text-xs">Priorité</th>
              <th className="text-right p-3 text-gray-500 font-mono text-xs">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.charges.map(c => (
              <tr key={c.charge_id} className="border-t border-gray-800/50">
                <td className="p-3 text-white font-semibold">{c.service}</td>
                <td className="p-3 text-gray-400 text-xs">{c.description}</td>
                <td className="p-3 text-right text-white font-mono">{c.monthly_amount.toFixed(0)} €</td>
                <td className="p-3">
                  <button onClick={() => toggleStatus(c)} className={`px-2 py-0.5 rounded text-xs font-mono border ${STATUS_COLORS_FINANCE[c.status] || ''}`}>
                    {c.status === 'active' ? '● Actif' : c.status === 'pending' ? '○ À activer' : '− Inactif'}
                  </button>
                </td>
                <td className="p-3 text-gray-400 text-xs font-mono">{c.priority}</td>
                <td className="p-3 text-right">
                  <Button onClick={() => edit(c)} variant="outline" size="sm" className="border-gray-700 text-cyan-400 mr-1">Éditer</Button>
                  <Button onClick={() => removeCharge(c.charge_id)} variant="outline" size="sm" className="border-red-500/50 text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-900/80">
            <tr>
              <td colSpan={2} className="p-3 text-right text-white font-bold">Total Actif</td>
              <td className="p-3 text-right text-green-400 font-bold font-mono">{data.totals?.active_monthly?.toFixed(0) || 0} €</td>
              <td colSpan={3}></td>
            </tr>
            <tr>
              <td colSpan={2} className="p-3 text-right text-gray-500">+ Scénario complet (scale)</td>
              <td className="p-3 text-right text-gray-400 font-mono">{data.totals?.full_scale_monthly?.toFixed(0) || 0} €</td>
              <td colSpan={3}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ============ TAB NAVIGATION ============
// Organisation en 2 zones visibles + 1 dropdown "Société" pour la config peu utilisée
// Email (Inbox IA) retiré du site — la gestion mail repasse dans Cowork

const TABS_OPERATIONS = [
  { id: 'missions',       label: 'Missions',       icon: Target,    color: 'cyan' },
  { id: 'support',        label: 'Support',        icon: Ticket,    color: 'magenta' },
  { id: 'audit_billing',  label: 'Audit Billing',  icon: FileText,  color: 'success' },
];

const TABS_AGENTS = [
  { id: 'agent',          label: 'Agent IA',        icon: Bot,        color: 'magenta' },
  { id: 'agent_perf',     label: 'Performance IA',  icon: Gauge,      color: 'magenta' },
  { id: 'pros_codes',     label: 'Pros & Codes',    icon: Briefcase,  color: 'cyan' },
  { id: 'notifications',  label: 'Notifications',   icon: Mail,       color: 'cyan' },
];

// Société = tout ce qui sert à la gestion interne / config peu fréquente
const TABS_SOCIETE = [
  { id: 'finance',        label: 'Finance',          icon: DollarSign,    color: 'success' },
  { id: 'parrainage',     label: 'Parrainage & Fraude', icon: Shield,    color: 'yellow' },
  { id: 'security',       label: 'Sécurité (2FA)',   icon: Shield,        color: 'cyan' },
  { id: 'maintenance',    label: 'Maintenance',      icon: AlertTriangle, color: 'yellow' },
  { id: 'test_runner',    label: 'Tests E2E',        icon: Play,          color: 'success' },
];

const TABS = [...TABS_OPERATIONS, ...TABS_AGENTS, ...TABS_SOCIETE];


// ============ COMPOSANT BARRE DE NAVIGATION ============
// 3 zones visuelles distinctes :
//   1. OPÉRATIONS (toujours visible, avec point pulsant) — taches quotidiennes
//   2. REVENUS / CRM (visible) — vue hebdo
//   3. SYSTÈME (dropdown caché par défaut) — config rare
const COLOR_MAP = {
  cyan:    { active: 'text-cyan-400 border-cyan-400',       glow: '0 0 20px rgba(0,245,255,0.3)',  hover: 'hover:text-cyan-400 hover:border-cyan-400/50' },
  success: { active: 'text-green-400 border-green-400',     glow: '0 0 20px rgba(0,255,148,0.3)',  hover: 'hover:text-green-400 hover:border-green-400/50' },
  magenta: { active: 'text-fuchsia-400 border-fuchsia-400', glow: '0 0 20px rgba(255,0,255,0.3)',  hover: 'hover:text-fuchsia-400 hover:border-fuchsia-400/50' },
  yellow:  { active: 'text-yellow-400 border-yellow-400',   glow: '0 0 20px rgba(234,179,8,0.3)',  hover: 'hover:text-yellow-400 hover:border-yellow-400/50' },
};

const TabButton = ({ tab, isActive, onClick, badge = 0 }) => {
  const IconComp = tab.icon;
  const colors = COLOR_MAP[tab.color] || COLOR_MAP.cyan;
  return (
    <button
      onClick={onClick}
      className={`
        relative flex items-center gap-2 px-4 py-3 font-orbitron text-xs uppercase tracking-wider
        border-b-2 transition-all duration-200 whitespace-nowrap flex-shrink-0
        ${isActive ? `${colors.active} bg-white/5` : `text-gray-400 border-transparent ${colors.hover}`}
      `}
      style={isActive ? { boxShadow: colors.glow } : {}}
    >
      <IconComp className="w-4 h-4" />
      {tab.label}
      {badge > 0 && (
        <span
          className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
          title={`${badge} action(s) en attente`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
};

const AdminTabBar = ({ activeTab, setActiveTab, tabsOps, tabsAgents, tabsSociete }) => {
  const [societeOpen, setSocieteOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const isInSociete = tabsSociete.some(t => t.id === activeTab);

  // Charge les compteurs de notifs au mount + toutes les 30s
  useEffect(() => {
    let cancelled = false;
    const loadCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/admin/notif-counts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setCounts(res.data.counts || {});
      } catch {
        // Silent — pas critique
      }
    };
    loadCounts();
    const interval = setInterval(loadCounts, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Total badge sur "Société" = somme des counts des onglets dedans
  const societeBadge = tabsSociete.reduce((sum, t) => sum + (counts[t.id] || 0), 0);

  // Auto-close du dropdown quand on clique ailleurs
  useEffect(() => {
    if (!societeOpen) return;
    const close = (e) => {
      if (!e.target.closest('[data-societe-menu]')) setSocieteOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [societeOpen]);

  return (
    <div className="max-w-7xl mx-auto px-4">
      <div className="flex items-center gap-1 pb-0 flex-wrap">
        {/* Section 1 : OPÉRATIONS — actions quotidiennes */}
        {tabsOps.map(tab => (
          <TabButton key={tab.id} tab={tab} isActive={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)} badge={counts[tab.id] || 0} />
        ))}

        <div className="w-px h-8 bg-gray-800 mx-1" />

        {/* Section 2 : AGENTS & CRM */}
        {tabsAgents.map(tab => (
          <TabButton key={tab.id} tab={tab} isActive={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)} badge={counts[tab.id] || 0} />
        ))}

        <div className="w-px h-8 bg-gray-800 mx-1" />

        {/* Section 3 : SOCIÉTÉ — dropdown unique pour la config interne */}
        <div className="relative" data-societe-menu>
          <button
            onClick={(e) => { e.stopPropagation(); setSocieteOpen(!societeOpen); }}
            className={`
              relative flex items-center gap-2 px-4 py-3 font-orbitron text-xs uppercase tracking-wider
              border-b-2 transition-all duration-200 whitespace-nowrap
              ${isInSociete
                ? 'text-yellow-400 border-yellow-400 bg-white/5'
                : 'text-gray-400 border-transparent hover:text-yellow-400 hover:border-yellow-400/50'}
            `}
            style={isInSociete ? { boxShadow: '0 0 20px rgba(234,179,8,0.3)' } : {}}
          >
            <Settings className="w-4 h-4" />
            {isInSociete ? tabsSociete.find(t => t.id === activeTab)?.label : 'Société'}
            {societeBadge > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                {societeBadge > 99 ? '99+' : societeBadge}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform ${societeOpen ? 'rotate-180' : ''}`} />
          </button>

          {societeOpen && (
            <div className="absolute top-full right-0 mt-1 bg-gray-900 border border-gray-700 rounded shadow-2xl z-50 min-w-[220px]">
              <div className="text-[9px] font-mono text-gray-500 uppercase tracking-widest px-4 pt-3 pb-1">
                // gestion société
              </div>
              {tabsSociete.map(tab => {
                const IconComp = tab.icon;
                const colors = COLOR_MAP[tab.color] || COLOR_MAP.cyan;
                const isActive = activeTab === tab.id;
                const badge = counts[tab.id] || 0;
                return (
                  <button
                    key={tab.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTab(tab.id);
                      setSocieteOpen(false);
                    }}
                    className={`
                      w-full flex items-center gap-2 px-4 py-3 text-left text-xs font-orbitron uppercase tracking-wider
                      ${isActive ? `${colors.active} bg-white/5` : `text-gray-400 hover:text-white hover:bg-gray-800`}
                    `}
                  >
                    <IconComp className="w-4 h-4" />
                    <span className="flex-1">{tab.label}</span>
                    {badge > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// V1.2 — ProsAndCodesPanel : Gestion des pros partenaires + codes promo
// ═══════════════════════════════════════════════════════════════════════
const ProsAndCodesPanel = () => {
  const [pros, setPros] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPro, setSelectedPro] = useState(null);
  const [proClients, setProClients] = useState([]);
  const [showNewProForm, setShowNewProForm] = useState(false);
  const [showNewCodeForm, setShowNewCodeForm] = useState(false);
  const [newPro, setNewPro] = useState({ name: '', email: '', phone: '', profession: '', company: '', notes: '' });
  const [newCode, setNewCode] = useState({
    type: 'pro', percent_off: 20, prefix: 'PRO',
    description: '', max_uses: 0,
    allowed_packs: ['mission_demenagement', 'mission_heritage']
  });
  const [codeGenForm, setCodeGenForm] = useState({ percent_off: 20, offert: false, description: '', max_uses: 0 });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [prosRes, codesRes] = await Promise.all([
        axios.get(`${API}/admin/pros`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPros(prosRes.data.pros || []);
      setCodes(codesRes.data.codes || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchProClients = async (pro) => {
    setSelectedPro(pro);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/pros/${pro.pro_id}/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setProClients(res.data.clients || []);
    } catch (e) {
      toast.error('Erreur chargement clients');
    }
  };

  const handleCreatePro = async () => {
    if (!newPro.name.trim() || !newPro.email.trim()) {
      toast.error('Nom et email obligatoires');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/pros`, newPro, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Pro créé');
      setShowNewProForm(false);
      setNewPro({ name: '', email: '', phone: '', profession: '', company: '', notes: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleGenerateProCode = async (proId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/pros/${proId}/generate-code`, codeGenForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Code créé : ${res.data.promo.code}`);
      fetchAll();
      if (selectedPro) fetchProClients(selectedPro);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleCreateGenericCode = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/coupons`, newCode, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Code créé : ${res.data.promo.code}`);
      setShowNewCodeForm(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleCode = async (code, active) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/coupons/${code}`, { active: !active }, { headers: { Authorization: `Bearer ${token}` } });
      fetchAll();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDeleteCode = async (code) => {
    if (!window.confirm(`Supprimer définitivement le code ${code} ?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/coupons/${code}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Code supprimé');
      fetchAll();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code ${code} copié`);
  };

  if (loading) {
    return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-1 h-8 bg-cyan-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(0,245,255,0.8)' }} />
        <h2 className="font-orbitron text-xl font-black text-white">Pros Partenaires & Codes Promo</h2>
        <span className="font-mono text-xs text-cyan-400/60">// PRESCRIPTEURS · CRM · CODES</span>
      </div>

      {/* V1.2 — Guide rapide */}
      <div className="glass-card p-4 border border-cyan-400/20 bg-cyan-400/5 text-sm text-gray-300 leading-relaxed">
        <p className="font-orbitron text-cyan-400 text-xs uppercase mb-2 tracking-widest">// Guide rapide</p>
        <ul className="space-y-1.5 text-xs md:text-sm">
          <li><span className="text-cyan-400 font-bold">1.</span> <span className="text-white font-semibold">Créer un pro</span> (notaire, agent immo, CGP...) via le bouton <span className="text-cyan-400 font-mono">Nouveau pro</span>.</li>
          <li><span className="text-cyan-400 font-bold">2.</span> Cliquer sur <span className="inline-block w-3 h-3 bg-cyan-400/20 rounded align-middle"></span> dans la ligne du pro → <span className="text-white font-semibold">générer un code</span>. Choisir <span className="text-success font-mono">-X%</span> pour une remise, ou cocher <span className="text-yellow-400 font-mono">Pack OFFERT</span> si c'est le pro qui paye pour son client.</li>
          <li><span className="text-cyan-400 font-bold">3.</span> Le code est <span className="text-white font-semibold">créé en base L'EFFACEUR + chez Stripe</span> automatiquement. Il suffit de le transmettre au client, qui le saisit sur la page de paiement.</li>
          <li><span className="text-cyan-400 font-bold">4.</span> Chaque client qui utilise le code est <span className="text-white font-semibold">lié au pro</span> dans l'onglet — traçabilité CRM complète (qui a amené qui, combien a été payé).</li>
          <li><span className="text-cyan-400 font-bold">5.</span> Pour une <span className="text-white font-semibold">promo générale</span> (pas liée à un pro), utiliser <span className="text-magenta-400 font-mono">Nouveau code</span> en bas de la liste codes.</li>
        </ul>
      </div>

      {/* ============ BLOC PROS ============ */}
      <div className="glass-card border border-cyan-400/20 overflow-hidden">
        <div className="bg-gray-900/80 p-4 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Briefcase className="w-5 h-5 text-cyan-400" />
            <h3 className="font-orbitron text-sm font-bold text-white">Pros partenaires ({pros.length})</h3>
          </div>
          <Button size="sm" onClick={() => setShowNewProForm(!showNewProForm)} className="bg-cyan-400/20 text-cyan-400 hover:bg-cyan-400/30 border border-cyan-400/40">
            <UserPlus className="w-4 h-4 mr-2" /> Nouveau pro
          </Button>
        </div>

        {showNewProForm && (
          <div className="p-4 bg-gray-900/50 border-b border-gray-800 grid md:grid-cols-3 gap-3">
            <Input placeholder="Nom *" value={newPro.name} onChange={e => setNewPro({...newPro, name: e.target.value})} />
            <Input placeholder="Email *" value={newPro.email} onChange={e => setNewPro({...newPro, email: e.target.value})} />
            <Input placeholder="Téléphone" value={newPro.phone} onChange={e => setNewPro({...newPro, phone: e.target.value})} />
            <Input placeholder="Profession (notaire, agent immo...)" value={newPro.profession} onChange={e => setNewPro({...newPro, profession: e.target.value})} />
            <Input placeholder="Société" value={newPro.company} onChange={e => setNewPro({...newPro, company: e.target.value})} />
            <Input placeholder="Notes" value={newPro.notes} onChange={e => setNewPro({...newPro, notes: e.target.value})} />
            <div className="md:col-span-3 flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowNewProForm(false)}>Annuler</Button>
              <Button size="sm" onClick={handleCreatePro} className="bg-cyan-400 text-black hover:bg-cyan-300">Créer</Button>
            </div>
          </div>
        )}

        <table className="w-full text-sm">
          <thead className="bg-gray-900/50">
            <tr>
              <th className="text-left p-3 text-gray-500 font-mono text-xs">Pro</th>
              <th className="text-left p-3 text-gray-500 font-mono text-xs">Profession</th>
              <th className="text-center p-3 text-gray-500 font-mono text-xs">Clients</th>
              <th className="text-center p-3 text-gray-500 font-mono text-xs">CA apporté</th>
              <th className="text-right p-3 text-gray-500 font-mono text-xs">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pros.length > 0 ? pros.map((p) => (
              <tr key={p.pro_id} className="border-t border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                <td className="p-3">
                  <div className="text-white font-bold">{p.name}</div>
                  <div className="text-gray-500 text-xs font-mono">{p.email}</div>
                </td>
                <td className="p-3 text-gray-300">
                  {p.profession || '—'}
                  {p.company && <div className="text-gray-500 text-xs">{p.company}</div>}
                </td>
                <td className="p-3 text-center text-cyan-400 font-bold">{p.clients_count || 0}</td>
                <td className="p-3 text-center text-success font-bold">{(p.total_paid_amount || 0).toFixed(0)}€</td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" onClick={() => fetchProClients(p)} className="bg-cyan-400/20 text-cyan-400 hover:bg-cyan-400/30 h-7 px-2" title="Voir clients">
                      <Eye className="w-3 h-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="5" className="p-6 text-center text-gray-600 font-mono text-xs">// AUCUN_PRO — cliquez sur "Nouveau pro"</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ============ DETAIL PRO SÉLECTIONNÉ ============ */}
      {selectedPro && (
        <div className="glass-card border border-cyan-400/30 bg-cyan-400/5 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-orbitron text-base font-bold text-white">{selectedPro.name}</h3>
              <p className="text-gray-400 text-sm font-mono">{selectedPro.email} — {selectedPro.profession}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setSelectedPro(null)}>Fermer</Button>
          </div>

          {/* Génération code pour ce pro */}
          <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 mb-4">
            <p className="text-cyan-400 font-bold text-sm mb-3">Générer un code pour ce pro</p>
            <div className="grid md:grid-cols-4 gap-2">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Réduction %</label>
                <Input type="number" min="1" max="100" value={codeGenForm.percent_off} onChange={e => setCodeGenForm({...codeGenForm, percent_off: parseInt(e.target.value) || 20, offert: false})} disabled={codeGenForm.offert} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Usage max (0 = ∞)</label>
                <Input type="number" min="0" value={codeGenForm.max_uses} onChange={e => setCodeGenForm({...codeGenForm, max_uses: parseInt(e.target.value) || 0})} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-gray-500 block mb-1">Description (optionnel)</label>
                <Input value={codeGenForm.description} onChange={e => setCodeGenForm({...codeGenForm, description: e.target.value})} />
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={codeGenForm.offert} onChange={e => setCodeGenForm({...codeGenForm, offert: e.target.checked, percent_off: e.target.checked ? 100 : 20})} />
                <Star className="w-4 h-4 text-yellow-400" />
                Pack OFFERT (le pro paye, le client entre gratis)
              </label>
              <Button size="sm" onClick={() => handleGenerateProCode(selectedPro.pro_id)} className="bg-cyan-400 text-black hover:bg-cyan-300">
                Générer code
              </Button>
            </div>
          </div>

          {/* Clients de ce pro */}
          <p className="text-cyan-400 font-bold text-sm mb-2">Clients apportés ({proClients.length})</p>
          <div className="bg-gray-900/60 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/80">
                <tr>
                  <th className="text-left p-2 text-gray-500 font-mono text-xs">Client</th>
                  <th className="text-left p-2 text-gray-500 font-mono text-xs">Pack</th>
                  <th className="text-center p-2 text-gray-500 font-mono text-xs">Montant</th>
                  <th className="text-center p-2 text-gray-500 font-mono text-xs">Payé par pro</th>
                  <th className="text-right p-2 text-gray-500 font-mono text-xs">Date</th>
                </tr>
              </thead>
              <tbody>
                {proClients.length > 0 ? proClients.map((c, idx) => (
                  <tr key={idx} className="border-t border-gray-800/50">
                    <td className="p-2">
                      <div className="text-white">{c.client_name}</div>
                      <div className="text-gray-500 text-xs font-mono">{c.client_email}</div>
                    </td>
                    <td className="p-2 text-gray-300 text-xs">{c.pack_type}</td>
                    <td className="p-2 text-center text-success font-bold">{(c.amount_paid || 0).toFixed(0)}€</td>
                    <td className="p-2 text-center">{c.paid_by_pro ? <Star className="w-4 h-4 text-yellow-400 inline" /> : '—'}</td>
                    <td className="p-2 text-right text-gray-500 text-xs">{c.linked_at?.slice(0, 10)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="5" className="p-4 text-center text-gray-600 font-mono text-xs">// AUCUN_CLIENT_ENCORE</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ============ BLOC CODES PROMO ============ */}
      <div className="glass-card border border-magenta-400/20 overflow-hidden">
        <div className="bg-gray-900/80 p-4 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Ticket className="w-5 h-5 text-magenta-400" />
            <h3 className="font-orbitron text-sm font-bold text-white">Codes promo ({codes.length})</h3>
          </div>
          <Button size="sm" onClick={() => setShowNewCodeForm(!showNewCodeForm)} className="bg-magenta-400/20 text-magenta-400 hover:bg-magenta-400/30 border border-magenta-400/40">
            <Ticket className="w-4 h-4 mr-2" /> Nouveau code
          </Button>
        </div>

        {showNewCodeForm && (
          <div className="p-4 bg-gray-900/50 border-b border-gray-800 grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Type</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" value={newCode.type} onChange={e => setNewCode({...newCode, type: e.target.value})}>
                <option value="pro">Pro</option>
                <option value="pro_offert">Pro offert (-100%)</option>
                <option value="parrainage">Parrainage</option>
                <option value="lancement">Lancement</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Réduction %</label>
              <Input type="number" min="1" max="100" value={newCode.percent_off} onChange={e => setNewCode({...newCode, percent_off: parseInt(e.target.value) || 0})} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Préfixe code</label>
              <Input value={newCode.prefix} onChange={e => setNewCode({...newCode, prefix: e.target.value.toUpperCase()})} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500 block mb-1">Description</label>
              <Input value={newCode.description} onChange={e => setNewCode({...newCode, description: e.target.value})} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Usage max (0 = ∞)</label>
              <Input type="number" min="0" value={newCode.max_uses} onChange={e => setNewCode({...newCode, max_uses: parseInt(e.target.value) || 0})} />
            </div>
            <div className="md:col-span-3 flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowNewCodeForm(false)}>Annuler</Button>
              <Button size="sm" onClick={handleCreateGenericCode} className="bg-magenta-400 text-black hover:bg-magenta-300">Créer le code</Button>
            </div>
          </div>
        )}

        <table className="w-full text-sm">
          <thead className="bg-gray-900/50">
            <tr>
              <th className="text-left p-3 text-gray-500 font-mono text-xs">Code</th>
              <th className="text-left p-3 text-gray-500 font-mono text-xs">Type</th>
              <th className="text-center p-3 text-gray-500 font-mono text-xs">Réduction</th>
              <th className="text-center p-3 text-gray-500 font-mono text-xs">Utilisations</th>
              <th className="text-center p-3 text-gray-500 font-mono text-xs">Statut</th>
              <th className="text-right p-3 text-gray-500 font-mono text-xs">Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.length > 0 ? codes.map((c, idx) => (
              <tr key={idx} className="border-t border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-cyan-400 font-mono font-bold">{c.code}</span>
                    <button onClick={() => handleCopyCode(c.code)} className="text-gray-500 hover:text-cyan-400" title="Copier"><Copy className="w-3 h-3" /></button>
                  </div>
                  {c.description && <div className="text-gray-500 text-xs mt-0.5">{c.description}</div>}
                </td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                    c.type === 'pro_offert' ? 'bg-yellow-500/20 text-yellow-400' :
                    c.type === 'pro' ? 'bg-cyan-500/20 text-cyan-400' :
                    c.type === 'parrainage' ? 'bg-magenta-500/20 text-magenta-400' :
                    c.type === 'lancement' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>{c.type}</span>
                </td>
                <td className="p-3 text-center text-success font-bold">-{c.percent_off}%</td>
                <td className="p-3 text-center text-white">
                  {c.uses_count || 0}{c.max_uses > 0 ? ` / ${c.max_uses}` : ''}
                </td>
                <td className="p-3 text-center">
                  <span className={`px-2 py-0.5 rounded text-xs font-mono ${c.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {c.active ? '● ACTIF' : '○ INACTIF'}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" onClick={() => handleToggleCode(c.code, c.active)} className="bg-gray-700/40 text-gray-300 hover:bg-gray-700 h-7 px-2" title={c.active ? 'Désactiver' : 'Activer'}>
                      {c.active ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                    </Button>
                    <Button size="sm" onClick={() => handleDeleteCode(c.code)} className="bg-red-500/20 text-red-400 hover:bg-red-500/30 h-7 px-2" title="Supprimer">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="6" className="p-6 text-center text-gray-600 font-mono text-xs">// AUCUN_CODE</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [clicksByPartner, setClicksByPartner] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tab persisté dans l'URL (?tab=missions) → F5 garde l'onglet courant
  const navigate = useNavigate();
  const initialTab = (() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    return TABS.some(x => x.id === t) ? t : 'missions';
  })();
  const [activeTab, setActiveTabState] = useState(initialTab);
  const setActiveTab = (id) => {
    setActiveTabState(id);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', id);
    window.history.replaceState(null, '', `/admin?${params.toString()}`);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Si l'URL change manuellement (back/forward), on resync le tab
  useEffect(() => {
    const onPop = () => {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('tab');
      if (t && TABS.some(x => x.id === t)) setActiveTabState(t);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, clicksRes, commissionsRes, referrersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/overview`),
        axios.get(`${API}/admin/dashboard/clicks-by-partner`),
        axios.get(`${API}/admin/dashboard/commissions`),
        axios.get(`${API}/admin/dashboard/fraud-detection`)
      ]);
      
      setOverview(overviewRes.data);
      setClicksByPartner(clicksRes.data.data || []);
      setCommissions(commissionsRes.data.commissions || []);
      setTopReferrers(referrersRes.data.top_referrers || []);
    } catch (error) {
      console.error('Erreur chargement admin:', error);
      toast.error('Erreur de chargement des données admin');
    } finally {
      setLoading(false);
    }
  };
  
  const handleApproveCommission = async (conversionId) => {
    try {
      await axios.post(`${API}/admin/dashboard/approve-commission/${conversionId}`);
      toast.success('Commission approuvée');
      fetchAllData();
    } catch {
      toast.error('Erreur lors de l\'approbation');
    }
  };
  
  const handleRejectCommission = async (conversionId) => {
    try {
      await axios.post(`${API}/admin/dashboard/reject-commission/${conversionId}?reason=Fraude suspectée`);
      toast.success('Commission rejetée');
      fetchAllData();
    } catch {
      toast.error('Erreur lors du rejet');
    }
  };
  
  const handleApproveReferral = async (referralId) => {
    try {
      await axios.post(`${API}/referral/admin/approve`, { referral_id: referralId });
      toast.success('Récompense approuvée');
      fetchAllData();
    } catch {
      toast.error('Erreur lors de l\'approbation');
    }
  };
  
  const handleSuspendReferral = async (referralId) => {
    try {
      await axios.post(`${API}/referral/admin/suspend`, { referral_id: referralId, reason: 'Activité suspecte' });
      toast.success('Parrainage suspendu');
      fetchAllData();
    } catch {
      toast.error('Erreur lors de la suspension');
    }
  };
  
  const exportCSV = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export téléchargé');
    } catch {
      toast.error('Erreur lors de l\'export');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }
  
  const activeTabConfig = TABS.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-void text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-black/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-gray-500 hover:text-cyan-400 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <span className="text-gray-700">|</span>
            <h1 className="font-orbitron text-lg font-black tracking-wider">
              <span className="text-cyan-400">L'</span>
              <span className="text-white">EFFACEUR</span>
              <span className="text-gray-500 text-sm ml-2 font-mono">// ADMIN</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <NotificationCenter />
            <Button onClick={fetchAllData} variant="outline" size="sm" className="border-gray-700 text-gray-400 hover:text-white hover:border-cyan-400/50">
              <RefreshCw className="w-4 h-4 mr-1" />Sync
            </Button>
            <Button onClick={() => exportCSV('commissions')} variant="outline" size="sm" className="border-gray-700 text-gray-400 hover:text-white">
              <Download className="w-4 h-4 mr-1" />CSV
            </Button>
          </div>
        </div>

        {/* Tab Navigation — Opérations + Agents + dropdown Société */}
        <AdminTabBar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabsOps={TABS_OPERATIONS}
          tabsAgents={TABS_AGENTS}
          tabsSociete={TABS_SOCIETE}
        />
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">

        {/* ═══ ONGLET 1 : GESTION DES MISSIONS ═══ */}
        {activeTab === 'missions' && (
          <motion.div key="missions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-8 bg-cyan-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(0,245,255,0.8)' }} />
              <h2 className="font-orbitron text-xl font-black text-white">Gestion des Missions</h2>
              <span className="font-mono text-xs text-cyan-400/60">// PRIORITÉ_1</span>
            </div>
            <MissionManagementPanel />
          </motion.div>
        )}

        {/* ═══ ONGLET 2 : MÉTRIQUES & AFFILIATION ═══ */}
        {activeTab === 'metriques' && (
          <motion.div key="metriques" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-8 bg-green-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(0,255,148,0.8)' }} />
              <h2 className="font-orbitron text-xl font-black text-white">Métriques</h2>
              <span className="font-mono text-xs text-green-400/60">// KPIs · PARRAINAGE · AFFILIATION_SI_ACTIVE</span>
            </div>

            {/* V1.2 — KPIs principaux (missions + revenus) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Missions totales', value: overview?.missions?.total || 0, color: 'text-cyan-400', border: 'border-cyan-400/30' },
                { label: 'Missions en cours', value: overview?.missions?.active || 0, color: 'text-yellow-400', border: 'border-yellow-400/30' },
                { label: 'Missions terminées', value: overview?.missions?.completed || 0, color: 'text-green-400', border: 'border-green-400/30' },
                { label: 'Revenus totaux', value: `${overview?.revenue?.total || 0}€`, color: 'text-fuchsia-400', border: 'border-fuchsia-400/30' }
              ].map((kpi, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: idx * 0.05 }}
                  className={`glass-card p-4 border ${kpi.border}`}>
                  <p className="text-gray-500 text-xs font-mono mb-2">{kpi.label}</p>
                  <p className={`text-2xl font-orbitron font-black ${kpi.color}`}>{kpi.value}</p>
                </motion.div>
              ))}
            </div>

            {/* Parrainage — visible en permanence */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="glass-card p-4 border border-fuchsia-400/30">
                <h3 className="font-orbitron text-sm text-fuchsia-400 mb-3 flex items-center gap-2"><Gift className="w-4 h-4" />PARRAINAGE</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-900/50 p-2 rounded"><p className="text-gray-500 text-xs">Codes actifs</p><p className="text-cyan-400 font-bold">{overview?.referral?.total_codes || 0}</p></div>
                  <div className="bg-gray-900/50 p-2 rounded"><p className="text-gray-500 text-xs">Filleuls</p><p className="text-cyan-400 font-bold">{overview?.referral?.total_referrals || 0}</p></div>
                  <div className="bg-gray-900/50 p-2 rounded"><p className="text-gray-500 text-xs">En attente</p><p className="text-yellow-400 font-bold">{overview?.referral?.pending_rewards || 0}€</p></div>
                  <div className="bg-gray-900/50 p-2 rounded"><p className="text-gray-500 text-xs">Payés</p><p className="text-green-400 font-bold">{overview?.referral?.paid_rewards || 0}€</p></div>
                </div>
              </div>
              {/* Affiliation — visible seulement si des clics existent */}
              {clicksByPartner.length > 0 && (
                <div className="glass-card p-4 border border-cyan-400/30">
                  <h3 className="font-orbitron text-sm text-cyan-400 mb-3 flex items-center gap-2"><Zap className="w-4 h-4" />AFFILIATION</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-900/50 p-2 rounded"><p className="text-gray-500 text-xs">En attente</p><p className="text-yellow-400 font-bold">{overview?.affiliation?.pending || 0}</p></div>
                    <div className="bg-gray-900/50 p-2 rounded"><p className="text-gray-500 text-xs">Approuvées</p><p className="text-green-400 font-bold">{overview?.affiliation?.approved || 0}</p></div>
                  </div>
                </div>
              )}
            </div>

            {/* Tracker conversions — seulement si clics présents */}
            {clicksByPartner.length > 0 && <ConversionTracker data={overview} clicksByPartner={clicksByPartner} />}

            {/* Clics par partenaire — visible seulement si des clics existent */}
            {clicksByPartner.length > 0 && (
              <section>
                <h3 className="font-orbitron text-sm text-gray-400 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-cyan-400" />CLICS PAR PARTENAIRE
                </h3>
                <div className="glass-card border border-gray-800 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-900/50">
                      <tr>
                        <th className="text-left p-3 text-gray-500 font-mono text-xs">Partenaire</th>
                        <th className="text-left p-3 text-gray-500 font-mono text-xs">Réseau</th>
                        <th className="text-center p-3 text-gray-500 font-mono text-xs">Clics</th>
                        <th className="text-center p-3 text-gray-500 font-mono text-xs">Conv.</th>
                        <th className="text-right p-3 text-gray-500 font-mono text-xs">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clicksByPartner.map((p, idx) => (
                        <tr key={idx} className="border-t border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                          <td className="p-3 text-white font-medium">{p.partner}</td>
                          <td className="p-3 text-gray-400 font-mono text-xs">{p.network}</td>
                          <td className="p-3 text-center text-cyan-400 font-bold">{p.total_clicks}</td>
                          <td className="p-3 text-center text-green-400 font-bold">{p.conversions}</td>
                          <td className="p-3 text-right text-fuchsia-400 font-bold">{p.commission?.toFixed(2)}€</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Commissions — visible seulement si données présentes */}
            {commissions.length > 0 && (
              <section>
                <h3 className="font-orbitron text-sm text-gray-400 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />GESTION DES COMMISSIONS
                </h3>
                <div className="glass-card border border-gray-800 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-900/50">
                      <tr>
                        <th className="text-left p-3 text-gray-500 font-mono text-xs">ID</th>
                        <th className="text-left p-3 text-gray-500 font-mono text-xs">Partenaire</th>
                        <th className="text-center p-3 text-gray-500 font-mono text-xs">Commission</th>
                        <th className="text-center p-3 text-gray-500 font-mono text-xs">Statut</th>
                        <th className="text-right p-3 text-gray-500 font-mono text-xs">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.slice(0, 10).map((c, idx) => (
                        <tr key={idx} className="border-t border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                          <td className="p-3 text-gray-500 font-mono text-xs">{c.conversion_id?.slice(0, 12)}...</td>
                          <td className="p-3 text-white">{c.partner || c.network}</td>
                          <td className="p-3 text-center text-green-400 font-bold">{c.commission?.toFixed(2)}€</td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                              c.status === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                              c.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                              'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}>{c.status}</span>
                          </td>
                          <td className="p-3 text-right">
                            {c.status === 'pending' && (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" onClick={() => handleApproveCommission(c.conversion_id)} className="bg-green-500/20 text-green-400 hover:bg-green-500/30 h-7 px-2"><CheckCircle className="w-3 h-3" /></Button>
                                <Button size="sm" onClick={() => handleRejectCommission(c.conversion_id)} className="bg-red-500/20 text-red-400 hover:bg-red-500/30 h-7 px-2"><XCircle className="w-3 h-3" /></Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </motion.div>
        )}

        {/* ═══ ONGLET 3 : WEBHOOKS ═══ */}
        {activeTab === 'webhooks' && (
          <motion.div key="webhooks" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-8 bg-fuchsia-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(255,0,255,0.8)' }} />
              <h2 className="font-orbitron text-xl font-black text-white">Webhooks</h2>
              <span className="font-mono text-xs text-fuchsia-400/60">// MAKE · ZAPIER · N8N</span>
            </div>
            <WebhookConfigPanel />
          </motion.div>
        )}

        {/* ═══ ONGLET 5 : PROS & CODES PROMO ═══ */}
        {activeTab === 'pros_codes' && (
          <motion.div key="pros_codes" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <ProsAndCodesPanel />
          </motion.div>
        )}

        {/* ═══ ONGLET 6 : AGENT / OPÉRATEUR ═══ */}
        {activeTab === 'agent' && (
          <motion.div key="agent" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <AgentSuperPanel />
          </motion.div>
        )}

        {/* INBOX IA retiré — gestion mail déléguée à Cowork */}

        {/* ═══ ONGLET 7 : FINANCE ═══ */}
        {activeTab === 'finance' && (
          <motion.div key="finance" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <FinancePanel />
          </motion.div>
        )}

        {/* ═══ ONGLET 4 : PARRAINAGE & FRAUDE ═══ */}
        {activeTab === 'parrainage' && (
          <motion.div key="parrainage" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-8 bg-yellow-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(234,179,8,0.8)' }} />
              <h2 className="font-orbitron text-xl font-black text-white">Parrainage & Fraude</h2>
              <span className="font-mono text-xs text-yellow-400/60">// TOP_PARRAINS · DÉTECTION</span>
            </div>
            <div className="glass-card border border-yellow-400/20 overflow-hidden">
              <div className="bg-gray-900/80 p-4 border-b border-gray-800 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <h3 className="font-orbitron text-sm font-bold text-white">Détection Fraude — Top 10 Parrains</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="text-left p-3 text-gray-500 font-mono text-xs">Code</th>
                    <th className="text-left p-3 text-gray-500 font-mono text-xs">Utilisateur</th>
                    <th className="text-center p-3 text-gray-500 font-mono text-xs">Parrainages</th>
                    <th className="text-center p-3 text-gray-500 font-mono text-xs">Récompenses</th>
                    <th className="text-center p-3 text-gray-500 font-mono text-xs">Risque</th>
                    <th className="text-right p-3 text-gray-500 font-mono text-xs">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {topReferrers.length > 0 ? topReferrers.map((r, idx) => (
                    <tr key={idx} className="border-t border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                      <td className="p-3 text-cyan-400 font-mono font-bold">{r.code}</td>
                      <td className="p-3 text-white">{r.user_name || r.user_id?.slice(0, 8) + '...'}</td>
                      <td className="p-3 text-center text-white font-bold">{r.total_referrals}</td>
                      <td className="p-3 text-center">
                        <span className="text-yellow-400 font-bold">{r.pending_rewards}€</span>
                        <span className="text-gray-600 mx-1">/</span>
                        <span className="text-green-400 font-bold">{r.paid_rewards}€</span>
                      </td>
                      <td className="p-3 text-center">
                        {(() => {
                          const riskClass = r.fraud_risk === 'high' ? 'bg-red-500/20 text-red-400 border-red-500/30' : r.fraud_risk === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30';
                          const riskLabel = r.fraud_risk === 'high' ? 'ELEVE' : r.fraud_risk === 'medium' ? 'MOYEN' : 'FAIBLE';
                          return (<span className={'px-2 py-0.5 rounded text-xs font-mono border ' + riskClass}>{riskLabel}</span>);
                        })()}
                      </td>
                      <td className="p-3 text-right">
                        {r.status === 'active' && (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" onClick={() => handleApproveReferral(r.code)} className="bg-green-500/20 text-green-400 hover:bg-green-500/30 h-7 px-2" title="Valider"><CheckCircle className="w-3 h-3" /></Button>
                            <Button size="sm" onClick={() => handleSuspendReferral(r.code)} className="bg-red-500/20 text-red-400 hover:bg-red-500/30 h-7 px-2" title="Suspendre"><XCircle className="w-3 h-3" /></Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6" className="p-6 text-center text-gray-600 font-mono text-xs">// AUCUN_PARRAIN</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* ═══ ONGLET AUDIT BILLING ═══ */}
        {activeTab === 'audit_billing' && (
          <motion.div key="audit_billing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <AuditBillingPanel />
          </motion.div>
        )}

        {/* ═══ ONGLET SUPPORT TICKETS ═══ */}
        {activeTab === 'support' && (
          <motion.div key="support" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <SupportTicketsPanel />
          </motion.div>
        )}

        {/* ═══ ONGLET SÉCURITÉ (2FA) ═══ */}
        {activeTab === 'security' && (
          <motion.div key="security" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <TwoFactorSetup />
          </motion.div>
        )}

        {/* ═══ ONGLET MAINTENANCE ═══ */}
        {activeTab === 'maintenance' && (
          <motion.div key="maintenance" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <MaintenanceTogglePanel />
          </motion.div>
        )}

        {/* ═══ ONGLET TEST RUNNER E2E ═══ */}
        {activeTab === 'test_runner' && (
          <motion.div key="test_runner" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <TestRunnerPanel />
          </motion.div>
        )}

        {/* ═══ ONGLET NOTIFICATIONS ═══ */}
        {activeTab === 'notifications' && (
          <motion.div key="notifications" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <NotificationsTogglePanel />
          </motion.div>
        )}

        {/* Footer */}
        <div className="pt-12 pb-6 flex justify-center border-t border-gray-800/30 mt-8">
          <ToulouseSignature variant="footer" />
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
